import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSSE } from "../hooks/useSSE";
import {
  BedDouble, Plus, Search, ChevronLeft, ChevronRight, RefreshCw, LogIn, LogOut,
  ShoppingCart, Receipt, Printer, BarChart3, Trash2, Edit2,
  X, Save, Loader2, User, CalendarDays, Users,
  CheckCircle, Clock, XCircle, ChevronDown, ChevronUp, FileText
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../App";
import ProductoSearchSelect from "../components/ProductoSearchSelect";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { EntidadModal } from "./CatastroPage";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL  = { disponible: "Disponible", reservado: "Reservado", ocupado: "Ocupado", por_facturar: "Por facturar" };
const STATUS_COLOR  = {
  disponible: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  reservado:  "bg-amber-500/15 text-amber-400 border border-amber-500/30",
  ocupado:    "bg-red-500/15 text-red-400 border border-red-500/30",
  por_facturar: "bg-violet-500/15 text-violet-400 border border-violet-500/30",
};
const STATUS_LINE   = { disponible: "bg-emerald-500", reservado: "bg-amber-400", ocupado: "bg-red-500", por_facturar: "bg-violet-500" };

const RES_STATUS_LABEL = { pendiente: "Pendiente", en_curso: "En curso", pendiente_factura: "Pend. Factura", finalizada: "Finalizada", cancelada: "Cancelada" };
const RES_STATUS_COLOR = {
  pendiente:          "bg-amber-500/15 text-amber-400",
  en_curso:           "bg-emerald-500/15 text-emerald-400",
  pendiente_factura:  "bg-violet-500/15 text-violet-400",
  finalizada:         "bg-slate-500/15 text-slate-400",
  cancelada:          "bg-red-500/15 text-red-400",
};

const ORIGENES_GASTO = ["Frigobar", "Restaurant", "Lavandería", "Otro"];
const TIPOS_DOC      = ["C.I.P.", "Pasaporte", "RUC", "Cédula extranjera", "Otro"];
const TIPOS_HAB      = ["Single Económica","Single Estándar","Single Ejecutiva","Doble Estándar","Matrimonial Estándar","Matrimonial Premium","Triple Familiar","Cuádruple"];
const PRECIOS_HAB    = { "Single Económica":[120000,70000],"Single Estándar":[150000,90000],"Single Ejecutiva":[200000,130000],"Doble Estándar":[250000,140000],"Matrimonial Estándar":[280000,160000],"Matrimonial Premium":[350000,200000],"Triple Familiar":[450000,180000],"Cuádruple":[600000,160000] };
// Capacidad por defecto según tipo (para habitaciones sin capacidad guardada)
const CAP_HAB        = { "Single Económica":1,"Single Estándar":1,"Single Ejecutiva":1,"Doble Estándar":2,"Matrimonial Estándar":2,"Matrimonial Premium":2,"Triple Familiar":3,"Cuádruple":4 };
// Horarios por defecto por tipo [checkin, checkout]
const HORARIO_HAB    = { "Cuádruple":["09:30","09:30"] };

function fmt(n) { if (n == null) return "—"; return Number(n).toLocaleString("es-PY"); }
function fmtFecha(s) { if (!s) return "—"; try { const [y,m,d]=s.split("-"); return `${d}/${m}/${y}`; } catch { return s; } }
function noches(fe, fs) { if (!fe || !fs) return 0; return Math.max(1, Math.round((new Date(fs)-new Date(fe))/86400000)); }
function CuentaOptions({ cuentas = [] }) {
  const labels = { PYG: "Cuenta bancaria Guaraníes (PYG)", USD: "Cuenta bancaria Dólares (USD)", BRL: "Cuenta bancaria Reales (BRL)", ARS: "Cuenta bancaria Pesos (ARS)" };
  const activas = (cuentas || []).filter(c => c.activa !== false);
  const monedasBase = ["PYG", "USD", "BRL", "ARS"];
  const monedasExtra = Array.from(new Set(activas.map(c => c.moneda || "PYG"))).filter(m => !monedasBase.includes(m));
  return (
    <>
      {[...monedasBase, ...monedasExtra].map(moneda => {
        const grupo = activas.filter(c => (c.moneda || "PYG") === moneda);
        if (!grupo.length) return null;
        return (
          <optgroup key={moneda} label={labels[moneda] || moneda}>
            {grupo.map(c => (
              <option key={c.id} value={c.id}>
                {c.nombre}{c.banco ? ` · ${c.banco}` : ""} ({c.moneda || "PYG"}){c.es_predeterminada ? " ★" : ""}
              </option>
            ))}
          </optgroup>
        );
      })}
    </>
  );
}

// ── ErrorBoundary — captura crashes de render sin blank screen ────────────────

class TabErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("[HotelPage] render error:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <XCircle className="w-8 h-8 text-red-400 opacity-60" />
          <p className="text-red-400 text-sm font-semibold">Error al cargar esta sección</p>
          <p className="text-slate-500 text-xs max-w-xs text-center">{this.state.error?.message || "Error desconocido"}</p>
          <button
            onClick={() => this.setState({ error: null })}
            className="mt-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ClienteSearchSelect = ({ clientes, value, onChange, onClienteCreado, puedeCrear = true }) => {
  const [query, setQuery]             = useState("");
  const [open, setOpen]               = useState(false);
  const [crearNombre, setCrearNombre] = useState("");
  const { token, activeEmpresaPropia } = useContext(AuthContext);

  const filtered = useMemo(() => {
    const lista = Array.isArray(clientes) ? clientes : [];
    if (!query.trim()) return lista.slice(0, 12);
    const q = query.toLowerCase();
    return lista.filter(c =>
      (c.nombre || "").toLowerCase().includes(q) ||
      (c.razon_social || "").toLowerCase().includes(q) ||
      (c.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [clientes, query]);

  const listaClientes = Array.isArray(clientes) ? clientes : [];
  const exactMatch = listaClientes.some(c =>
    (c.nombre || c.razon_social || "").toLowerCase() === query.trim().toLowerCase() ||
    (c.ruc || "") === query.trim()
  );
  const selected = listaClientes.find(c => c.id === value) || null;

  const handleSelect = (cli) => {
    onChange(cli.id);
    setQuery("");
    setOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150);
  };

  const handleSaved = (cliente) => {
    setCrearNombre("");
    if (cliente?.id) {
      onClienteCreado?.(cliente);
      handleSelect(cliente);
    }
  };

  const LABEL = "Responsable financiero (opcional — quien paga la estadía)";

  // ── Cliente ya seleccionado → chip con X ──────────────────────────────────
  if (selected) {
    return (
      <div>
        <label className="block text-xs text-slate-400 mb-1">{LABEL}</label>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 min-h-[38px] bg-arandu-dark border-cyan-500/30">
          <div className="flex-1 min-w-0">
            <span className="text-white text-sm font-medium truncate block">{selected.nombre || selected.razon_social}</span>
            <span className="text-xs text-slate-400">{selected.ruc ? `RUC: ${selected.ruc}` : "Cliente de Catastro"}</span>
          </div>
          <button type="button" onClick={() => { onChange(""); setQuery(""); }}
            className="text-slate-500 hover:text-white shrink-0 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Buscador con dropdown ─────────────────────────────────────────────────
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{LABEL}</label>
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
          <input value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); setCreando(false); }}
            onFocus={() => setOpen(true)}
            onBlur={handleBlur}
            placeholder="Buscar por nombre o RUC…"
            className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-500" />
        </div>
        {open && (
          <div className="absolute z-50 w-full mt-1 bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl overflow-hidden">
            {filtered.length > 0 && (
              <ul className="max-h-48 overflow-y-auto divide-y divide-white/5">
                {filtered.map(c => (
                  <li key={c.id}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSelect(c)}
                    className="px-3 py-2.5 hover:bg-white/5 cursor-pointer flex items-center justify-between">
                    <span className="text-white text-sm">{c.nombre || c.razon_social}</span>
                    {c.ruc && <span className="text-slate-500 text-xs ml-2 shrink-0">RUC: {c.ruc}</span>}
                  </li>
                ))}
              </ul>
            )}
            {filtered.length === 0 && query.trim() && (
              <div className="px-3 py-2 text-slate-500 text-xs">Sin resultados para "{query.trim()}"</div>
            )}
            {puedeCrear && query.trim() && !exactMatch && (
              <div className={filtered.length > 0 ? "border-t border-white/10" : ""}>
                <button type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setCrearNombre(query.trim()); setOpen(false); }}
                  className="w-full px-3 py-2.5 text-left text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors">
                  <Plus className="w-3.5 h-3.5 shrink-0" />
                  <span>Crear "{query.trim()}" como cliente en Catastro</span>
                </button>
              </div>
            )}
            <div className="border-t border-white/5 px-3 py-2">
              <button type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(""); setQuery(""); setOpen(false); }}
                className="text-slate-500 text-xs hover:text-slate-300 transition-colors">
                — Dejar sin asignar —
              </button>
            </div>
          </div>
        )}
      </div>
      {crearNombre && (
        <EntidadModal
          entidad={{ _presetRol: "es_cliente", _initialNombre: crearNombre, _initialRazonSocial: crearNombre }}
          token={token}
          logo={activeEmpresaPropia?.slug}
          allowedRoles={{ es_cliente: true }}
          onClose={() => setCrearNombre("")}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────

export default function HotelPage() {
  const navigate = useNavigate();
  const { token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const eqs   = activeEmpresaPropia?.id   ? `?empresa_id=${encodeURIComponent(activeEmpresaPropia.id)}`   : "";
  const logoQ = activeEmpresaPropia?.slug ? `&logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : "";

  const canVerPanel       = hasPermission("hoteleria.ver_panel");
  const canCrearHab       = hasPermission("hoteleria.crear_hab");
  const canEditarHab      = hasPermission("hoteleria.editar_hab");
  const canEliminarHab    = hasPermission("hoteleria.eliminar_hab");
  const canReserva        = hasPermission("hoteleria.reserva");
  const canEditarReserva  = hasPermission("hoteleria.editar_reserva");
  const canCancelarReserva= hasPermission("hoteleria.cancelar_reserva");
  const canCheckin        = hasPermission("hoteleria.checkin");
  const canGasto          = hasPermission("hoteleria.registrar_gasto");
  const canExtracto       = hasPermission("hoteleria.ver_extracto");
  const canCheckout          = hasPermission("hoteleria.checkout");
  const canReportes          = hasPermission("hoteleria.reportes");
  const canCrearCliente      = hasPermission("entidades.crear") || hasPermission("empresas.crear");
  const canAnticipo          = hasPermission("hoteleria.registrar_anticipo");
  const canVerAnticipos      = hasPermission("hoteleria.ver_anticipos");
  const canVerHuespedes      = hasPermission("hoteleria.ver_huespedes");
  const canEditarHuespedes   = hasPermission("hoteleria.editar_huespedes");
  const canEliminarHuespedes = hasPermission("hoteleria.eliminar_huespedes");

  // ── SSE — eventos en tiempo real ─────────────────────────────────────────
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "reserva_actualizada") {
        // Re-fetch reservas para reflejar el cambio
        // (las reservas son complejas, mejor fetch completo)
        setReservas(prev => {
          const updated = prev.map(r => r.id === event.data.id ? { ...r, estado: event.data.estado } : r);
          return updated;
        });
      }
    }, []), // eslint-disable-line
  });

  const [tab, setTab]                   = useState("panel");
  const [habitaciones, setHabitaciones] = useState([]);   // catalog
  const [panel, setPanel]               = useState([]);   // enriched rooms for panel
  const [reservas, setReservas]         = useState([]);
  const [clientes, setClientes]         = useState([]);
  const [productos, setProductos]       = useState([]);
  const [cuentas, setCuentas]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroResStatus, setFiltroResStatus] = useState("activas");

  // Modals
  const [modalReserva, setModalReserva]   = useState(null); // null | "nuevo" | reserva_obj
  const [modalExtracto, setModalExtracto] = useState(null);
  const [modalGasto, setModalGasto]       = useState(null); // reserva_id
  const [modalCheckout, setModalCheckout] = useState(null);
  const [modalHab, setModalHab]           = useState(null);
  const [modalAnticipo, setModalAnticipo] = useState(null); // reserva_id
  const [saving, setSaving]               = useState(false);

  // Huéspedes frecuentes
  const [huespedes, setHuespedes]           = useState([]);
  const [huespedesQ, setHuespedesQ]         = useState("");
  const [loadingHuespedes, setLoadingHuespedes] = useState(false);

  // Anticipos
  const [anticipos, setAnticipos]             = useState([]);
  const [anticiposQ, setAnticipsoQ]           = useState("");
  const [anticipoFiltroAplicado, setAnticipFiltroAplicado] = useState("todos"); // todos | pendiente | aplicado
  const [loadingAnticipos, setLoadingAnticipos] = useState(false);

  // Anticipo form
  const [anticipoForm, setAnticipo] = useState({ monto: 0, metodo: "efectivo", cuenta_id: "", notas: "" });

  // Forms
  const EMPTY_RES = () => {
    const hoy = new Date().toISOString().slice(0,10);
    const man = new Date(Date.now()+86400000).toISOString().slice(0,10);
    return { fecha_entrada: hoy, fecha_salida: man, responsable_financiero_id: "", motivo_viaje: "", notas: "", habitaciones: [] };
  };
  const [resForm, setResForm]   = useState(EMPTY_RES());
  const [gastoForm, setGastoForm] = useState({ habitacion_numero: "", producto_id: "", descripcion: "", cantidad: 1, precio_unitario: 0, origen: "Frigobar" });
  const [coForm, setCoForm]     = useState({ numero: "", fecha: new Date().toISOString().slice(0,10), forma_pago: "contado", cuenta_id: "" });
  const [habForm, setHabForm]   = useState({});

  // Disponibilidad para selector de habitaciones en reserva
  const [disponibles, setDisponibles] = useState([]);
  const [loadingDisp, setLoadingDisp] = useState(false);
  const [dropdownHabsOpen, setDropdownHabsOpen] = useState(false);
  const dropdownHabsRef = useRef(null);

  // Cerrar dropdown de habitaciones al clickear fuera
  useEffect(() => {
    if (!dropdownHabsOpen) return;
    function handleClick(e) {
      if (dropdownHabsRef.current && !dropdownHabsRef.current.contains(e.target)) {
        setDropdownHabsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownHabsOpen]);

  // Auto-refresh silencioso cada 30 segundos — sincroniza con otros usuarios sin interrumpir modales abiertos
  useEffect(() => {
    const INTERVALO = 30_000;
    const id = setInterval(() => {
      // No interrumpir al usuario si tiene un modal abierto
      if (modalReserva || modalExtracto || modalGasto || modalCheckout || modalHab || modalAnticipo) return;
      fetchPanel();
      fetchReservas(filtroResStatus);
    }, INTERVALO);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalReserva, modalExtracto, modalGasto, modalCheckout, modalHab, filtroResStatus]);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchPanel = useCallback(async () => {
    if (!canVerPanel) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/hotel/panel${eqs}`, { headers });
      if (r.ok) {
        const data = await r.json();
        setPanel(Array.isArray(data) ? data.map(h => ({
          ...h,
          precio_habitacion: h.precio_habitacion ?? 0,
          precio_persona: h.precio_persona ?? 0,
          horario_checkin: h.horario_checkin ?? "14:00",
          horario_checkout: h.horario_checkout ?? "10:00",
          status: h.status ?? "disponible",
        })) : []);
      }
    } catch { /* silencioso en auto-refresh */ }
    finally { setLoading(false); }
  }, [token, activeEmpresaPropia?.id]);

  const fetchHabitaciones = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/hotel/habitaciones${eqs}`, { headers });
      if (r.ok) setHabitaciones(await r.json());
    } catch { /* silencioso */ }
  }, [token, activeEmpresaPropia?.id]);

  const fetchReservas = useCallback(async (statusFilter = null) => {
    if (!canVerPanel) return;
    try {
      let url = `${API}/admin/hotel/reservas${eqs}`;
      if (statusFilter && statusFilter !== "activas" && statusFilter !== "todas") {
        url += `${eqs ? "&" : "?"}status=${statusFilter}`;
      }
      const r = await fetch(url, { headers });
      if (r.ok) {
        let data = await r.json();
        if (!Array.isArray(data)) data = [];
        if (statusFilter === "activas") data = data.filter(x => ["pendiente","en_curso","pendiente_factura"].includes(x.status));
        // Normalizar cada reserva para que ningún campo null/undefined cause crash
        setReservas(data.map(x => ({
          ...x,
          id: x.id ?? x._id ?? "",
          status: x.status ?? "pendiente",
          fecha_entrada: typeof x.fecha_entrada === "string" ? x.fecha_entrada : "",
          fecha_salida:  typeof x.fecha_salida  === "string" ? x.fecha_salida  : "",
          responsable_nombre: x.responsable_nombre ?? "",
          motivo_viaje: x.motivo_viaje ?? "",
          // Filtrar nulls dentro del array de habitaciones
          habitaciones: Array.isArray(x.habitaciones)
            ? x.habitaciones.filter(Boolean).map(h => ({
                habitacion_numero:    h.habitacion_numero    ?? "",
                tipo_habitacion:      h.tipo_habitacion      ?? "",
                huesped_nombre:       h.huesped_nombre       ?? "",
                celular:              h.celular              ?? "",
                huespedes_adicionales: Array.isArray(h.huespedes_adicionales)
                  ? h.huespedes_adicionales.map(ha =>
                      typeof ha === "string"
                        ? { nombre: ha, tipo_doc: "C.I.P.", nro_doc: "", nacionalidad: "Paraguaya", procedencia: "", celular: "" }
                        : { nombre: ha.nombre ?? "", tipo_doc: ha.tipo_doc ?? "C.I.P.", nro_doc: ha.nro_doc ?? "", nacionalidad: ha.nacionalidad ?? "Paraguaya", procedencia: ha.procedencia ?? "", celular: ha.celular ?? "" }
                    )
                  : [],
                tipo_doc:             h.tipo_doc             ?? "",
                nro_doc:              h.nro_doc              ?? "",
                nacionalidad:         h.nacionalidad         ?? "",
                procedencia:          h.procedencia          ?? "",
                esquema_cobro:        h.esquema_cobro        ?? "habitacion",
                cantidad_personas:    h.cantidad_personas    ?? 1,
                valor_estadia:        h.valor_estadia        ?? 0,
              }))
            : [],
          total_general: x.total_general ?? 0,
          cantidad_dias: x.cantidad_dias ?? 0,
        })));
      }
    } catch { /* silencioso — no romper la UI si falla el fetch */ }
  }, [token, activeEmpresaPropia?.id]);

  const fetchClientes = useCallback(async () => {
    const r = await fetch(`${API}/admin/entidades?es_cliente=true&activo=true${activeEmpresaPropia?.slug ? `&logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : ""}`, { headers });
    if (r.ok) {
      const d = await r.json();
      setClientes(Array.isArray(d) ? d : d.empresas || []);
    }
  }, [token, activeEmpresaPropia?.slug]);

  const fetchProductos = useCallback(async () => {
    const r = await fetch(`${API}/admin/productos?limit=1000${logoQ}`, { headers });
    if (r.ok) { const d = await r.json(); setProductos(Array.isArray(d) ? d : d.productos || []); }
  }, [token, activeEmpresaPropia?.slug]);

  const fetchCuentas = useCallback(async () => {
    const slug = activeEmpresaPropia?.slug;
    if (!slug) { setCuentas([]); return; }
    const r = await fetch(`${API}/admin/cuentas-bancarias?logo_tipo=${encodeURIComponent(slug)}`, { headers });
    if (r.ok) setCuentas(await r.json());
  }, [token, activeEmpresaPropia?.slug]);

  const fetchHuespedes = useCallback(async () => {
    if (!canVerHuespedes) return;
    setLoadingHuespedes(true);
    try {
      const r = await fetch(`${API}/admin/hotel/huespedes${eqs}`, { headers });
      if (r.ok) setHuespedes(await r.json());
    } catch { } finally { setLoadingHuespedes(false); }
  }, [token, activeEmpresaPropia?.id, canVerHuespedes]); // eslint-disable-line

  const fetchAnticipos = useCallback(async () => {
    if (!canVerAnticipos) return;
    setLoadingAnticipos(true);
    try {
      const r = await fetch(`${API}/admin/hotel/anticipos${eqs}`, { headers });
      if (r.ok) setAnticipos(await r.json());
    } catch { } finally { setLoadingAnticipos(false); }
  }, [token, activeEmpresaPropia?.id, canVerAnticipos]); // eslint-disable-line

  const handleRegistrarAnticipo = async () => {
    if (!anticipoForm.monto || anticipoForm.monto <= 0) { toast.error("Ingrese un monto válido"); return; }
    if (!anticipoForm.cuenta_id) { toast.error("Seleccioná una cuenta bancaria para el anticipo"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${modalAnticipo}/anticipo${eqs}`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(anticipoForm),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Anticipo registrado");
      setModalAnticipo(null);
      // Refrescar extracto si está abierto
      if (modalExtracto) openExtracto(modalExtracto.reserva_id);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleEliminarHuesped = async (id) => {
    if (!confirm("¿Eliminar este huésped del historial?")) return;
    try {
      const r = await fetch(`${API}/admin/hotel/huespedes/${id}${eqs}`, { method: "DELETE", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Huésped eliminado");
      setHuespedes(prev => prev.filter(h => h.id !== id));
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    fetchPanel(); fetchHabitaciones(); fetchReservas(filtroResStatus); fetchClientes(); fetchProductos(); fetchCuentas();
    if (canVerHuespedes) fetchHuespedes();
  }, [token, activeEmpresaPropia?.id]); // eslint-disable-line

  useEffect(() => {
    if (tab === "reservas") fetchReservas(filtroResStatus);
    if (tab === "huespedes" && canVerHuespedes) fetchHuespedes();
    if (tab === "anticipos" && canVerAnticipos) fetchAnticipos();
  }, [tab, filtroResStatus]); // eslint-disable-line

  // ── Disponibilidad ────────────────────────────────────────────────────────

  const fetchDisponibilidad = useCallback(async (fe, fs, excludeId = null) => {
    if (!fe || !fs || fe >= fs) { setDisponibles([]); return; }
    setLoadingDisp(true);
    try {
      let url = `${API}/admin/hotel/disponibilidad${eqs}${eqs?"&":"?"}fecha_entrada=${fe}&fecha_salida=${fs}`;
      if (excludeId) url += `&exclude_reserva_id=${excludeId}`;
      const r = await fetch(url, { headers });
      if (r.ok) { const d = await r.json(); setDisponibles(d.disponibles || []); }
    } catch { } finally { setLoadingDisp(false); }
  }, [token, activeEmpresaPropia?.id]);

  // ── Filtros panel ─────────────────────────────────────────────────────────

  const panelFiltrado = panel.filter(h => filtroStatus === "todos" || h.status === filtroStatus);
  const counts = {
    todos: panel.length,
    disponible: panel.filter(h=>h.status==="disponible").length,
    reservado:  panel.filter(h=>h.status==="reservado").length,
    ocupado:    panel.filter(h=>h.status==="ocupado").length,
    por_facturar: panel.filter(h=>h.status==="por_facturar").length,
  };

  // ── Reserva form helpers ──────────────────────────────────────────────────

  const openNuevaReserva = (habNumero = null) => {
    const f = EMPTY_RES();
    if (habNumero) {
      const hab = habitaciones.find(h => h.numero === habNumero);
      if (hab) f.habitaciones = [emptyHuespedHab(hab)];
    }
    setResForm(f);
    fetchDisponibilidad(f.fecha_entrada, f.fecha_salida);
    setModalReserva("nuevo");
  };

  const openEditarReserva = (reserva) => {
    setResForm({
      fecha_entrada: reserva.fecha_entrada,
      fecha_salida: reserva.fecha_salida,
      responsable_financiero_id: reserva.responsable_financiero_id || "",
      motivo_viaje: reserva.motivo_viaje || "",
      notas: reserva.notas || "",
      // Enriquecer con metadata de precios/capacidad desde la lista local de habitaciones
      habitaciones: (reserva.habitaciones || []).map(h => {
        const habDef = habitaciones.find(hd => hd.numero === h.habitacion_numero);
        const cap = habDef?.capacidad || h.cantidad_personas || 1;
        const cantPersonas = h.cantidad_personas || 1;
        const existentes = (h.huespedes_adicionales || []).map(ha =>
          typeof ha === "string"
            ? { nombre: ha, tipo_doc: "C.I.P.", nro_doc: "", nacionalidad: "Paraguaya", procedencia: "", celular: "" }
            : { nombre: ha.nombre ?? "", tipo_doc: ha.tipo_doc ?? "C.I.P.", nro_doc: ha.nro_doc ?? "", nacionalidad: ha.nacionalidad ?? "Paraguaya", procedencia: ha.procedencia ?? "", celular: ha.celular ?? "" }
        );
        return {
          ...h,
          celular: h.celular ?? "",
          huespedes_adicionales: Array(Math.max(0, cantPersonas - 1))
            .fill(null)
            .map((_, i) => existentes[i] || { nombre: "", tipo_doc: "C.I.P.", nro_doc: "", nacionalidad: "Paraguaya", procedencia: "", celular: "" }),
          _tipo: habDef?.tipo || h.tipo_habitacion || "",
          _precio_habitacion: habDef?.precio_habitacion ?? 0,
          _precio_persona: habDef?.precio_persona ?? 0,
          _capacidad: cap,
        };
      }),
    });
    fetchDisponibilidad(reserva.fecha_entrada, reserva.fecha_salida, reserva.id);
    setModalReserva(reserva);
  };

  function emptyHuespedAdicional() {
    return { nombre: "", tipo_doc: "C.I.P.", nro_doc: "", nacionalidad: "Paraguaya", procedencia: "", celular: "" };
  }

  function emptyHuespedHab(hab) {
    const esquema = hab.regimen_cobro || "habitacion";
    const cap = hab.capacidad || 2;
    const cantPersonas = esquema === "habitacion" ? cap : 1;
    return {
      habitacion_numero: hab.numero,
      huesped_nombre: "",
      celular: "",
      huespedes_adicionales: Array(Math.max(0, cantPersonas - 1)).fill(null).map(() => emptyHuespedAdicional()),
      tipo_doc: "C.I.P.",
      nro_doc: "",
      nacionalidad: "Paraguaya",
      procedencia: "",
      esquema_cobro: esquema,
      cantidad_personas: cantPersonas,
      // metadata (not sent to backend)
      _tipo: hab.tipo,
      _precio_habitacion: hab.precio_habitacion,
      _precio_persona: hab.precio_persona,
      _capacidad: cap,
    };
  }

  const agregarHabitacion = (numero) => {
    const hab = habitaciones.find(h => h.numero === numero);
    if (!hab) return;
    if (resForm.habitaciones.find(h => h.habitacion_numero === numero)) {
      toast.warning("Esa habitación ya está en la reserva"); return;
    }
    setResForm(p => ({ ...p, habitaciones: [...p.habitaciones, emptyHuespedHab(hab)] }));
  };

  const quitarHabitacion = (numero) => {
    setResForm(p => ({ ...p, habitaciones: p.habitaciones.filter(h => h.habitacion_numero !== numero) }));
  };

  const updateHuespedHab = (numero, field, value) => {
    setResForm(p => ({
      ...p,
      habitaciones: p.habitaciones.map(h =>
        h.habitacion_numero === numero ? { ...h, [field]: value } : h
      ),
    }));
  };

  // Total estimado
  const totalEstimado = useMemo(() => {
    const n = noches(resForm.fecha_entrada, resForm.fecha_salida);
    return resForm.habitaciones.reduce((acc, h) => {
      const val = h.esquema_cobro === "persona"
        ? (h._precio_persona || 0) * (h.cantidad_personas || 1) * n
        : (h._precio_habitacion || 0) * n;
      return acc + val;
    }, 0);
  }, [resForm]);

  // ── CRUD Reserva ──────────────────────────────────────────────────────────

  const handleGuardarReserva = async () => {
    if (!resForm.habitaciones.length) { toast.error("Agregá al menos una habitación"); return; }
    if (resForm.habitaciones.some(h => !h.huesped_nombre.trim())) { toast.error("Todos los huéspedes principales deben tener nombre"); return; }
    if (resForm.habitaciones.some(h => !h.nro_doc?.trim())) { toast.error("El N° de documento del huésped principal es obligatorio"); return; }
    if (resForm.habitaciones.some(h => !h.celular?.trim())) { toast.error("El celular del huésped principal es obligatorio"); return; }
    if (resForm.habitaciones.some(h =>
      (h.huespedes_adicionales || []).some(ha => !ha.nombre?.trim())
    )) { toast.error("Completá el nombre de todos los huéspedes adicionales"); return; }
    if (resForm.habitaciones.some(h =>
      (h.huespedes_adicionales || []).some(ha => !ha.nro_doc?.trim())
    )) { toast.error("El N° de documento de todos los huéspedes adicionales es obligatorio"); return; }
    if (resForm.habitaciones.some(h =>
      (h.huespedes_adicionales || []).some(ha => !ha.celular?.trim())
    )) { toast.error("El celular de todos los huéspedes adicionales es obligatorio"); return; }
    setSaving(true);
    try {
      let responsableId = resForm.responsable_financiero_id || null;

      const isEdit = modalReserva && modalReserva !== "nuevo";
      const url  = isEdit ? `${API}/admin/hotel/reservas/${modalReserva.id}${eqs}` : `${API}/admin/hotel/reservas${eqs}`;
      const method = isEdit ? "PUT" : "POST";
      const body = {
        fecha_entrada: resForm.fecha_entrada,
        fecha_salida: resForm.fecha_salida,
        responsable_financiero_id: responsableId,
        motivo_viaje: resForm.motivo_viaje || null,
        notas: resForm.notas || null,
        habitaciones: resForm.habitaciones.map(({ _tipo, _precio_habitacion, _precio_persona, _capacidad, ...h }) => h),
      };
      const r = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success(isEdit ? "Reserva actualizada" : "Reserva creada");
      setModalReserva(null);
      fetchPanel(); fetchReservas(filtroResStatus);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleCancelarReserva = async (id) => {
    if (!confirm("¿Cancelar esta reserva?")) return;
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${id}${eqs}`, { method: "DELETE", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Reserva cancelada");
      fetchPanel(); fetchReservas(filtroResStatus);
    } catch (e) { toast.error(e.message); }
  };

  // ── Check-In ──────────────────────────────────────────────────────────────

  const handleCheckin = async (reservaId) => {
    if (!reservaId) { toast.error("ID de reserva inválido"); return; }
    if (!confirm("¿Confirmar check-in? Las habitaciones quedarán como ocupadas.")) return;
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${reservaId}/checkin${eqs}`, { method: "POST", headers });
      let body = null;
      try { body = await r.json(); } catch { /* respuesta no-JSON */ }
      if (!r.ok) throw new Error(body?.detail || `Error ${r.status}`);
      toast.success("Check-in realizado");
      setModalExtracto(null);
      await fetchPanel();
      await fetchReservas(filtroResStatus);
    } catch (e) { toast.error(e.message || "Error en check-in"); }
  };

  // ── Extracto ──────────────────────────────────────────────────────────────

  const openExtracto = async (reservaId) => {
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${reservaId}/extracto${eqs}`, { headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      setModalExtracto(await r.json());
    } catch (e) { toast.error(e.message); }
  };

  // ── Gasto ─────────────────────────────────────────────────────────────────

  const openGasto = (reservaId) => {
    const numHabs = (reservas.find(r => r.id === reservaId)?.habitaciones || []).map(h => h.habitacion_numero);
    setGastoForm({ habitacion_numero: numHabs[0] || "", producto_id: "", descripcion: "", cantidad: 1, precio_unitario: 0, origen: "Frigobar" });
    setModalGasto(reservaId);
  };

  const handleGasto = async () => {
    if (!gastoForm.descripcion.trim()) { toast.error("Descripción requerida"); return; }
    if (gastoForm.precio_unitario <= 0) { toast.error("Precio inválido"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${modalGasto}/gasto${eqs}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...gastoForm, cantidad: Number(gastoForm.cantidad), precio_unitario: Number(gastoForm.precio_unitario) }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Consumo registrado");
      setModalGasto(null);
      if (modalExtracto) openExtracto(modalExtracto.reserva_id);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const eliminarGasto = async (idx) => {
    if (!modalExtracto) return;
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${modalExtracto.reserva_id}/gasto/${idx}${eqs}`, { method: "DELETE", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Consumo eliminado");
      openExtracto(modalExtracto.reserva_id);
    } catch (e) { toast.error(e.message); }
  };

  // ── Checkout ──────────────────────────────────────────────────────────────

  // ── Número de factura automático (mismo timbrado/puntos que Ventas) ─────────
  const [tbConfig, setTbConfig] = useState(null);       // config de timbrado de la empresa
  const [loadingNumero, setLoadingNumero] = useState(false);
  const logoSlug = activeEmpresaPropia?.slug;
  const puntoSel = tbConfig?.puntos_expedicion?.find(p => p.codigo === coForm.punto_expedicion) || null;
  const numeroAuto = puntoSel?.modo_numeracion === "automatico";
  const timbradoOk = !!puntoSel?.vigente;

  const generarNumero = async (punto) => {
    if (!logoSlug || !punto) return;
    setLoadingNumero(true);
    try {
      const r = await fetch(`${API}/admin/siguiente-numero-factura/${logoSlug}`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ punto_expedicion: punto }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "No se pudo obtener el siguiente número"); return; }
      setCoForm(p => ({ ...p, numero: d.numero, punto_expedicion: punto }));
    } catch { toast.error("Error al obtener número automático"); }
    finally { setLoadingNumero(false); }
  };

  // Al abrir el modal de factura: cargar timbrado y, si el punto es automático, traer el número solo
  useEffect(() => {
    if (!modalCheckout || !logoSlug || !token) { setTbConfig(null); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API}/admin/timbrado-vigente/${logoSlug}`, { headers });
        const d = r.ok ? await r.json() : null;
        if (!vivo) return;
        if (!d?.tiene_config) { setTbConfig(null); return; }
        setTbConfig(d);
        const punto = d.punto_sugerido || d.puntos_expedicion?.[0]?.codigo || "001";
        setCoForm(p => ({ ...p, punto_expedicion: punto }));
        const pto = (d.puntos_expedicion || []).find(x => x.codigo === punto);
        if (pto?.modo_numeracion === "automatico" && pto?.vigente) generarNumero(punto);
      } catch { /* sin timbrado: queda número manual */ }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!modalCheckout, logoSlug, token]);

  // Reintentar la factura de una reserva que ya hizo check-out (quedó "Pend. Factura")
  const openFacturar = async (reservaId) => {
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${reservaId}/factura-prefill${eqs}`, { headers });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Error");
      setModalExtracto(null);
      setCoForm({ numero: "", fecha: new Date().toISOString().slice(0,10), forma_pago: "credito", cuenta_id: cuentas[0]?.id || "", punto_expedicion: "" });
      setModalCheckout({ ...d.factura_prefill, reserva_id: reservaId });
    } catch (e) { toast.error(e.message); }
  };

  const handleCheckout = async (reservaId) => {
    if (!confirm("¿Procesar check-out? Se generará la factura de hospedaje.")) return;
    try {
      const r = await fetch(`${API}/admin/hotel/reservas/${reservaId}/checkout${eqs}`, { method: "POST", headers });
      if (!r.ok) {
        const e = await r.json();
        const msg = Array.isArray(e.detail) ? e.detail.map(d => d.msg || JSON.stringify(d)).join("; ") : (e.detail || "Error");
        throw new Error(msg);
      }
      const data = await r.json();
      toast.success(data.mensaje);
      setModalExtracto(null);
      fetchPanel(); fetchReservas(filtroResStatus);
      setCoForm({ numero: "", fecha: new Date().toISOString().slice(0,10), forma_pago: "credito", cuenta_id: cuentas[0]?.id || "", punto_expedicion: "" });
      // Guardar reserva_id en el prefill para usarlo al confirmar checkout
      setModalCheckout({ ...data.factura_prefill, reserva_id: reservaId });
    } catch (e) { toast.error(e.message); }
  };

  const crearFacturaHotel = async () => {
    if (!coForm.numero.trim()) {
      toast.error(numeroAuto ? "Generá el número de factura con el botón «Generar N°»" : "Número de factura requerido");
      return;
    }
    if (tbConfig && puntoSel && !timbradoOk) { toast.error("El timbrado de este punto está vencido o sin configurar"); return; }
    setSaving(true);
    try {
      const total = modalCheckout.total || 0;
      const body = {
        tipo: "emitida",
        numero: coForm.numero,
        ...(coForm.punto_expedicion ? { punto_expedicion: coForm.punto_expedicion } : {}),
        fecha: coForm.fecha,
        forma_pago: "credito", // siempre crédito para poder aplicar anticipos como pago parcial
        cuenta_id: coForm.cuenta_id || null,
        empresa_id: modalCheckout.cliente.empresa_id || null,
        razon_social: modalCheckout.cliente.razon_social || "Huésped",
        ruc: modalCheckout.cliente.ruc || "",
        moneda: "PYG",
        monto: total,
        concepto: modalCheckout.notas || "Hospedaje",
        iva: Math.round(total / 11),
        conceptos: (modalCheckout.conceptos || []).map(c => ({ ...c, descuento_pct: 0 })),
        afecta_stock: false,
        logo_tipo: activeEmpresaPropia?.slug || "arandujar",
        notas: modalCheckout.notas || "",
      };
      const r = await fetch(`${API}/admin/facturas`, { method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) {
        const e = await r.json();
        const msg = Array.isArray(e.detail)
          ? e.detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e.detail || "Error al crear factura");
        throw new Error(msg);
      }
      const facturaCreada = await r.json();
      const facturaId = facturaCreada.id || facturaCreada.factura_id;

      // Confirmar checkout: aplica anticipos a la factura y marca la reserva como finalizada
      if (modalCheckout.reserva_id && facturaId) {
        const cr = await fetch(`${API}/admin/hotel/reservas/${modalCheckout.reserva_id}/confirmar-checkout${eqs}`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ factura_id: facturaId }),
        });
        if (!cr.ok) {
          const ce = await cr.json();
          // No bloqueamos — la factura ya fue creada; avisamos pero cerramos el modal
          toast.error(`Factura creada pero error al confirmar: ${ce.detail || "Error"}`);
        } else {
          toast.success("Factura creada y checkout confirmado");
        }
      } else {
        toast.success("Factura creada");
      }

      setModalCheckout(null);
      fetchPanel(); fetchReservas(filtroResStatus);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // ── CRUD Habitaciones ─────────────────────────────────────────────────────

  const openHabForm = (hab = null) => {
    setHabForm(hab ? { ...hab, capacidad: hab.capacidad ?? 2 } : { numero: "", tipo: "Single Estándar", precio_habitacion: 150000, precio_persona: 90000, regimen_cobro: "habitacion", capacidad: 2, horario_checkin: "14:00", horario_checkout: "10:00" });
    setModalHab(hab || {});
  };

  const handleSaveHab = async () => {
    if (!habForm.numero) { toast.error("Número requerido"); return; }
    setSaving(true);
    const isEdit = !!habForm.id;
    try {
      const url = isEdit ? `${API}/admin/hotel/habitaciones/${habForm.numero}${eqs}` : `${API}/admin/hotel/habitaciones${eqs}`;
      const r = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(habForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success(isEdit ? "Habitación actualizada" : "Habitación creada");
      setModalHab(null);
      fetchHabitaciones(); fetchPanel();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteHab = async (numero) => {
    if (!confirm(`¿Eliminar Habitación ${numero}?`)) return;
    try {
      const r = await fetch(`${API}/admin/hotel/habitaciones/${numero}${eqs}`, { method: "DELETE", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Habitación eliminada");
      fetchHabitaciones(); fetchPanel();
    } catch (e) { toast.error(e.message); }
  };

  // ── Fichas SENATUR ────────────────────────────────────────────────────────

  const imprimirFichasSenatur = (ext) => {
    const habs = ext.habitaciones || [];
    const mkFicha = (h, huesped) => `
      <div style="page-break-inside:avoid;margin-bottom:30px">
        <div class="hdr">
          <div class="titulo">Módulo Hotelero — Ficha de Registro de Huésped</div>
          <div style="font-size:12px;margin-top:4px">REGISTRO NACIONAL DE HUÉSPEDES (SENATUR)</div>
        </div>
        <div class="campo"><span><b>Habitación:</b> N° ${h.habitacion_numero}</span><span><b>Fecha:</b> ${new Date().toLocaleDateString("es-PY")}</span></div>
        <div class="campo"><span><b>Nombre / Razón Social:</b> ${huesped.nombre||"—"}</span></div>
        <div class="campo"><span><b>Tipo Doc.:</b> ${huesped.tipo_doc||"C.I.P."}</span><span><b>N° Documento:</b> ${huesped.nro_doc||"—"}</span></div>
        <div class="campo"><span><b>Nacionalidad:</b> ${huesped.nacionalidad||"Paraguaya"}</span><span><b>Procedencia:</b> ${huesped.procedencia||"—"}</span></div>
        <div class="campo"><span><b>Motivo:</b> ${ext.motivo_viaje||"—"}</span><span><b>Cant. personas:</b> ${h.cantidad_personas||1}</span></div>
        <div class="campo"><span><b>Check-In:</b> ${fmtFecha(ext.fecha_entrada)}</span><span><b>Check-Out Est.:</b> ${fmtFecha(ext.fecha_salida)}</span></div>
        <div style="font-size:11px;text-align:justify;border:1px solid #000;padding:10px;margin-top:20px">
          <b>Declaración Jurada:</b> Certifico que los datos provistos son verídicos, en cumplimiento con la normativa vigente de la República del Paraguay.
        </div>
        <div class="firma">
          <div class="linea-firma"><br>Firma del Huésped</div>
          <div class="linea-firma"><br>Firma del Recepcionista</div>
        </div>
      </div>
    `;
    const partes = [];
    habs.forEach(h => {
      // Ficha del titular
      partes.push(mkFicha(h, { nombre: h.huesped_nombre, tipo_doc: h.tipo_doc, nro_doc: h.nro_doc, nacionalidad: h.nacionalidad, procedencia: h.procedencia }));
      // Ficha por cada huésped adicional con datos guardados
      (h.huespedes_adicionales || []).forEach(ha => partes.push(mkFicha(h, ha)));
      // Fichas en blanco para huéspedes adicionales sin datos registrados
      const faltantes = (h.cantidad_personas || 1) - 1 - (h.huespedes_adicionales || []).length;
      for (let i = 0; i < faltantes; i++) {
        partes.push(mkFicha(h, { nombre: "(Completar manualmente)", tipo_doc: "—", nro_doc: "—", nacionalidad: "—", procedencia: "—" }));
      }
    });
    const w = window.open("","_blank");
    w.document.write(`<html><head><title>Fichas SENATUR</title><style>
      body{font-family:Arial,sans-serif;color:#000;padding:30px;line-height:1.6}
      .hdr{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:20px}
      .titulo{font-size:18px;font-weight:bold;text-transform:uppercase}
      .campo{display:flex;justify-content:space-between;border-bottom:1px dashed #ccc;padding-bottom:4px;margin-bottom:10px;font-size:14px}
      .firma{display:flex;justify-content:space-around;margin-top:60px;text-align:center}
      .linea-firma{border-top:1px solid #000;width:200px;padding-top:5px;font-size:12px;font-weight:bold}
    </style></head><body>${partes.join("")}</body></html>`);
    w.document.close(); w.onload = () => w.print();
  };

  const imprimirExtracto = (ext) => {
    const n = ext.cantidad_dias || 1;
    // Hospedaje — una fila por habitación
    const habLines = (ext.habitaciones||[]).map(h =>
      `<tr><td style="padding:6px;border-bottom:1px solid #ddd">Hospedaje Hab. ${h.habitacion_numero} (${h.tipo_habitacion||""}) — ${n} noche(s)</td>
       <td style="text-align:center;padding:6px;border-bottom:1px solid #ddd">1</td>
       <td style="text-align:right;padding:6px;border-bottom:1px solid #ddd">Gs. ${fmt(h.valor_estadia)}</td>
       <td style="text-align:right;padding:6px;border-bottom:1px solid #ddd;font-weight:bold">Gs. ${fmt(h.valor_estadia)}</td></tr>`
    ).join("");
    // Consumos — agrupados por habitación
    const gastosPorHab = {};
    (ext.gastos||[]).forEach(g => {
      const k = g.habitacion_numero || "__general__";
      if (!gastosPorHab[k]) gastosPorHab[k] = [];
      gastosPorHab[k].push(g);
    });
    const gLines = Object.entries(gastosPorHab).map(([hab, items]) => {
      const header = `<tr><td colspan="4" style="padding:4px 6px;background:#f0f0f0;font-size:10px;font-weight:bold;text-transform:uppercase;letter-spacing:.05em;color:#444">Consumos — ${hab === "__general__" ? "General" : `Hab. ${hab}`}</td></tr>`;
      const rows = items.map(g =>
        `<tr><td style="padding:5px 6px 5px 16px;border-bottom:1px solid #eee">[${g.origen}] ${g.descripcion}</td>
         <td style="text-align:center;padding:5px 6px;border-bottom:1px solid #eee">${g.cantidad}</td>
         <td style="text-align:right;padding:5px 6px;border-bottom:1px solid #eee">Gs. ${fmt(g.precio_unitario)}</td>
         <td style="text-align:right;padding:5px 6px;border-bottom:1px solid #eee;font-weight:bold">Gs. ${fmt(g.cantidad*g.precio_unitario)}</td></tr>`
      ).join("");
      return header + rows;
    }).join("");
    const w = window.open("","_blank");
    w.document.write(`<html><head><title>Extracto</title><style>body{font-family:'Courier New',monospace;padding:20px;color:#000}.hdr{text-align:center;border-bottom:2px solid #000;margin-bottom:15px}table{width:100%;border-collapse:collapse}th{font-size:11px;text-transform:uppercase;padding:5px;border-bottom:1px solid #000;text-align:left}.totales{text-align:right;margin-top:20px;border-top:2px solid #000;padding-top:8px;font-size:13px;line-height:1.8}</style></head><body>
      <div class="hdr"><div style="font-size:16px;font-weight:bold">Extracto de Cuenta</div>
      <div style="font-size:11px">${ext.fecha_entrada} → ${ext.fecha_salida} | ${n} noche(s)</div>
      <div style="font-size:10px">Impreso: ${new Date().toLocaleString("es-PY")}</div></div>
      <table><thead><tr><th>Concepto</th><th>Cant</th><th>P.Unit</th><th>Subtotal</th></tr></thead>
      <tbody>${habLines}${gLines}</tbody></table>
      <div class="totales">
        Subtotal hospedaje: Gs. ${fmt(ext.valor_total_hospedaje)}<br>
        Subtotal consumos: Gs. ${fmt(ext.total_gastos)}<br>
        <div style="font-size:16px;font-weight:bold;border-top:1px dashed #000;margin-top:4px;padding-top:4px">TOTAL: Gs. ${fmt(ext.total_general)}</div>
        <div style="font-size:11px;color:#555;margin-top:4px">IVA 10% incluido: Gs. ${fmt(ext.iva_10)}</div>
      </div>
      <div style="text-align:center;font-size:10px;margin-top:40px;border-top:1px solid #ccc;padding-top:8px">Documento interno — no válido como factura fiscal.</div>
    </body></html>`);
    w.document.close(); w.onload = () => w.print();
  };

  const imprimirExtractoTicket = (ext) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const fmtT = (n) => `₲ ${Math.round(n || 0).toLocaleString("es-PY")}`;
    const sep = (ch = "=", n = 40) => `<div class="sep">${ch.repeat(n)}</div>`;
    const now = new Date();
    const hora = now.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });
    const nNoches = ext.cantidad_dias || 1;

    const habItems = (ext.habitaciones || []).map(h => `
      <div class="item">
        <div class="item-top">
          <span class="item-cant">1</span>
          <span class="item-desc">Hospedaje Hab. ${h.habitacion_numero}${h.tipo_habitacion ? ` (${h.tipo_habitacion})` : ""} — ${nNoches} noche(s)</span>
          <span class="item-total">${fmtT(h.valor_estadia)}</span>
        </div>
        <div class="item-sub">${h.huesped_nombre || ""}</div>
      </div>`).join("");

    const gastosPorHab = {};
    (ext.gastos || []).forEach(g => {
      const k = g.habitacion_numero || "__general__";
      if (!gastosPorHab[k]) gastosPorHab[k] = [];
      gastosPorHab[k].push(g);
    });
    const gastoItems = Object.entries(gastosPorHab).map(([hab, items]) => {
      const header = `<div style="font-size:9px;font-weight:bold;text-transform:uppercase;background:#f0f0f0;padding:2px 4px;margin:3px 0;">— Consumos ${hab === "__general__" ? "General" : `Hab. ${hab}`} —</div>`;
      const rows = items.map(g => `
        <div class="item">
          <div class="item-top">
            <span class="item-cant">${g.cantidad}</span>
            <span class="item-desc">[${g.origen}] ${g.descripcion}</span>
            <span class="item-total">${fmtT(g.cantidad * g.precio_unitario)}</span>
          </div>
          ${g.cantidad > 1 ? `<div class="item-sub">${fmtT(g.precio_unitario)} c/u</div>` : ""}
        </div>`).join("");
      return header + rows;
    }).join("");

    w.document.write(`<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"/><title>Extracto Ticket</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Courier New',Courier,monospace;font-size:11px;color:#000;background:#fff;width:300px;margin:0 auto;padding:10px 6px 20px}
  @media print{@page{size:80mm auto;margin:0}body{width:80mm;padding:4px 4px 16px}}
  .center{text-align:center}.bold{font-weight:bold}.big{font-size:14px}
  .sep{border:none;border-top:1px dashed #888;margin:4px 0}
  .block{margin:3px 0}
  .item{margin:3px 0}
  .item-top{display:flex;justify-content:space-between;gap:4px}
  .item-cant{flex-shrink:0;min-width:18px}
  .item-desc{flex:1;word-break:break-word}
  .item-total{flex-shrink:0;text-align:right;font-weight:bold}
  .item-sub{font-size:9px;color:#555;padding-left:18px}
  .total-line{display:flex;justify-content:space-between;font-weight:bold;font-size:13px;margin:4px 0}
  .sub-line{display:flex;justify-content:space-between;font-size:10px;color:#555;margin:2px 0}
  .footer{text-align:center;font-size:9px;color:#777;margin-top:8px;border-top:1px dashed #aaa;padding-top:6px}
</style></head><body>
  <div class="center bold big">EXTRACTO DE CUENTA</div>
  <div class="center" style="font-size:9px">Documento interno — no válido como factura fiscal</div>
  ${sep()}
  <div class="block center">
    <div>${ext.fecha_entrada} → ${ext.fecha_salida}</div>
    <div style="font-size:9px">${nNoches} noche(s)</div>
  </div>
  ${ext.responsable_nombre ? `<div class="block center" style="font-size:10px">Resp: ${ext.responsable_nombre}</div>` : ""}
  ${sep()}
  ${habItems}
  ${gastoItems ? `${sep("-")}<div style="font-size:9px;color:#555;text-align:center;margin:2px 0">CONSUMOS</div>${gastoItems}` : ""}
  ${sep("=")}
  <div class="sub-line"><span>Hospedaje:</span><span>${fmtT(ext.valor_total_hospedaje)}</span></div>
  <div class="sub-line"><span>Consumos:</span><span>${fmtT(ext.total_gastos)}</span></div>
  <div class="total-line"><span>TOTAL:</span><span>${fmtT(ext.total_general)}</span></div>
  <div style="font-size:9px;color:#666;text-align:right">IVA 10%: ${fmtT(ext.iva_10)}</div>
  ${sep()}
  <div class="footer">Impreso: ${now.toLocaleDateString("es-PY")} ${hora}</div>
</body></html>`);
    w.document.close();
    w.onload = () => w.print();
  };

  // ── Tabs ──────────────────────────────────────────────────────────────────

  const TABS = [
    { id: "panel",        label: "Panel",        icon: BedDouble,    show: canVerPanel },
    { id: "reservas",     label: "Reservas",     icon: CalendarDays, show: canVerPanel },
    { id: "habitaciones", label: "Habitaciones", icon: Edit2,        show: canCrearHab || canEditarHab || canEliminarHab },
    { id: "historico",    label: "Histórico",    icon: BarChart3,    show: canReportes },
    { id: "anticipos",    label: "Anticipos",    icon: Receipt,      show: canVerAnticipos },
    { id: "huespedes",    label: "Huéspedes",    icon: Users,        show: canVerHuespedes },
  ].filter(t => t.show);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="bg-arandu-dark-light border-b border-white/5 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate("/sistema")} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <BedDouble className="w-6 h-6 text-cyan-400" />
        <div>
          <h1 className="text-lg font-bold text-white">Hotelería</h1>
          <p className="text-xs text-slate-400">Panel de administración hotelera</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canReserva && (
            <button onClick={() => openNuevaReserva()} className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors font-semibold">
              <Plus className="w-4 h-4" /> Nueva Reserva
            </button>
          )}
          <button onClick={() => { fetchPanel(); fetchReservas(filtroResStatus); }} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/5 px-6 flex gap-1 pt-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${tab === t.id ? "border-cyan-500 text-cyan-400 bg-cyan-500/5" : "border-transparent text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      <div className="p-6">

        {/* ── TAB PANEL ──────────────────────────────────────────────────── */}
        {tab === "panel" && (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {[
                { key:"todos",     label:"Todos",       color:"bg-white/10 text-slate-300 hover:bg-white/15" },
                { key:"disponible",label:"Disponibles", color:"bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" },
                { key:"reservado", label:"Reservados",  color:"bg-amber-500/15 text-amber-400 hover:bg-amber-500/25" },
                { key:"ocupado",   label:"Ocupados",    color:"bg-red-500/15 text-red-400 hover:bg-red-500/25" },
                ...(counts.por_facturar > 0 ? [{ key:"por_facturar", label:"Por facturar", color:"bg-violet-500/15 text-violet-400 hover:bg-violet-500/25" }] : []),
              ].map(f => (
                <button key={f.key} onClick={() => setFiltroStatus(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${filtroStatus===f.key ? f.color+" ring-1 ring-current" : f.color} border-white/10`}>
                  {f.label} <span className="ml-1 opacity-70">{counts[f.key]}</span>
                </button>
              ))}
              {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-500 ml-2" />}
            </div>

            {panelFiltrado.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <BedDouble className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{panel.length===0?"No hay habitaciones registradas":"Sin habitaciones con ese estado"}</p>
                {canCrearHab && panel.length===0 && (
                  <button onClick={() => { setTab("habitaciones"); openHabForm(); }} className="mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors">
                    + Agregar primera habitación
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {panelFiltrado.map(h => (
                  <PanelCard key={h.numero} hab={h}
                    onCheckin={canCheckin ? () => handleCheckin(h.reserva_id) : null}
                    onGasto={canGasto ? () => { openGasto(h.reserva_id); setReservas(prev => prev); } : null}
                    onExtracto={canExtracto ? () => openExtracto(h.reserva_id) : null}
                    onVerReserva={canVerPanel ? () => openExtracto(h.reserva_id) : null}
                    onAnticipo={canAnticipo ? () => { setModalAnticipo(h.reserva_id); setAnticipo({ monto: 0, metodo: "efectivo", cuenta_id: cuentas.find(c => (c.moneda||"PYG") === "PYG")?.id || "", notas: "" }); } : null}
                    onFacturar={canCheckout ? () => openFacturar(h.reserva_id) : null}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB RESERVAS ────────────────────────────────────────────────── */}
        {tab === "reservas" && (
          <TabErrorBoundary key="reservas-boundary">
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key:"activas",    label:"Activas" },
                { key:"pendiente",  label:"Pendientes" },
                { key:"en_curso",   label:"En curso" },
                { key:"finalizada", label:"Finalizadas" },
                { key:"todas",      label:"Todas" },
              ].map(f => (
                <button key={f.key} onClick={() => setFiltroResStatus(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filtroResStatus===f.key ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30 ring-1 ring-cyan-500/30" : "bg-white/5 text-slate-400 border-white/10 hover:bg-white/10"}`}>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {reservas.length === 0 ? (
                <div className="text-center py-12 text-slate-500 bg-arandu-dark-light border border-white/5 rounded-xl">
                  <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No hay reservas</p>
                </div>
              ) : reservas.map(res => (
                <ReservaRow key={res.id || Math.random()} res={res}
                  canCancelarReserva={canCancelarReserva} canEditarReserva={canEditarReserva} canCheckin={canCheckin} canExtracto={canExtracto} canGasto={canGasto} canAnticipo={canAnticipo}
                  onEditar={() => openEditarReserva(res)}
                  canCheckout={canCheckout} onFacturar={() => openFacturar(res.id)}
                  onCheckin={() => handleCheckin(res.id)}
                  onExtracto={() => openExtracto(res.id)}
                  onCancelar={() => handleCancelarReserva(res.id)}
                  onGasto={canGasto ? () => { setModalGasto(res.id); setGastoForm({ habitacion_numero: (res.habitaciones||[])[0]?.habitacion_numero||"", producto_id:"", descripcion:"", cantidad:1, precio_unitario:0, origen:"Frigobar" }); } : null}
                  onAnticipo={canAnticipo ? () => { setModalAnticipo(res.id); setAnticipo({ monto: 0, metodo: "efectivo", cuenta_id: cuentas.find(c => (c.moneda||"PYG") === "PYG")?.id || "", notas: "" }); } : null}
                />
              ))}
            </div>
          </div>
          </TabErrorBoundary>
        )}

        {/* ── TAB HABITACIONES ───────────────────────────────────────────── */}
        {tab === "habitaciones" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-400">{habitaciones.length} habitación(es)</p>
              {canCrearHab && (
                <button onClick={() => openHabForm()} className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors">
                  <Plus className="w-4 h-4" /> Nueva
                </button>
              )}
            </div>
            <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">N°</th>
                    <th className="px-4 py-3 text-left">Tipo</th>
                    <th className="px-4 py-3 text-right">P. Habitación</th>
                    <th className="px-4 py-3 text-right">P. Persona</th>
                    <th className="px-4 py-3 text-left">Régimen</th>
                    <th className="px-4 py-3 text-center">Cap.</th>
                    <th className="px-4 py-3 text-center">In/Out</th>
                    {(canEditarHab || canEliminarHab) && <th className="px-4 py-3 text-center">Acciones</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {habitaciones.map(h => (
                    <tr key={h.numero} className="hover:bg-white/3 transition-colors">
                      <td className="px-4 py-3 font-bold text-white">N° {h.numero}</td>
                      <td className="px-4 py-3 text-slate-300">{h.tipo}</td>
                      <td className="px-4 py-3 text-right text-emerald-400 font-mono">Gs. {fmt(h.precio_habitacion)}</td>
                      <td className="px-4 py-3 text-right text-cyan-400 font-mono">Gs. {fmt(h.precio_persona)}</td>
                      <td className="px-4 py-3 text-slate-400 capitalize">{h.regimen_cobro}</td>
                      <td className="px-4 py-3 text-center text-slate-400">{h.capacidad ?? CAP_HAB[h.tipo] ?? "—"}</td>
                      <td className="px-4 py-3 text-center text-xs text-slate-400">{h.horario_checkin} / {h.horario_checkout}</td>
                      {(canEditarHab || canEliminarHab) && (
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1">
                            {canEditarHab && <button onClick={() => openHabForm(h)} className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors" title="Editar"><Edit2 className="w-3.5 h-3.5" /></button>}
                            {canEliminarHab && <button onClick={() => handleDeleteHab(h.numero)} className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {habitaciones.length===0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No hay habitaciones</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB HISTÓRICO ──────────────────────────────────────────────── */}
        {tab === "historico" && <HistoricoTab eqs={eqs} headers={headers} canExtracto={canExtracto} onExtracto={openExtracto} />}

        {/* ── TAB ANTICIPOS ──────────────────────────────────────────────── */}
        {tab === "anticipos" && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button onClick={fetchAnticipos} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
              {["todos","pendiente","aplicado"].map(f => (
                <button key={f} onClick={() => setAnticipFiltroAplicado(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${anticipoFiltroAplicado===f ? "bg-violet-500/20 text-violet-400 border-violet-500/40 ring-1 ring-violet-400/30" : "text-slate-400 border-white/10 hover:text-white hover:bg-white/5"}`}>
                  {f === "todos" ? "Todos" : f === "pendiente" ? "Pendientes" : "Aplicados"}
                </button>
              ))}
              <span className="ml-auto text-xs text-slate-500">{anticipos.length} anticipo(s)</span>
            </div>
            {loadingAnticipos ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>
            ) : (
              <div className="bg-arandu-dark-light rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/5 text-xs text-slate-500 uppercase tracking-wider">
                      <th className="text-left px-4 py-3">Fecha</th>
                      <th className="text-left px-4 py-3">Hab.</th>
                      <th className="text-left px-4 py-3">Huésped</th>
                      <th className="text-left px-4 py-3">Método</th>
                      <th className="text-right px-4 py-3">Monto</th>
                      <th className="text-center px-4 py-3">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {anticipos
                      .filter(a => anticipoFiltroAplicado === "todos" ? true : anticipoFiltroAplicado === "aplicado" ? a.aplicado : !a.aplicado)
                      .map(a => (
                        <tr key={a.id} className="hover:bg-white/3 transition-colors">
                          <td className="px-4 py-3 text-slate-300">{fmtFecha(a.fecha)}</td>
                          <td className="px-4 py-3 text-slate-400">{a.habitaciones || "—"}</td>
                          <td className="px-4 py-3 text-slate-300">{a.huesped_nombre || "—"}</td>
                          <td className="px-4 py-3 text-slate-400 capitalize">{a.metodo}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-400">Gs. {fmt(a.monto)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${a.aplicado ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                              {a.aplicado ? "Aplicado" : "Pendiente"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    {anticipos.filter(a => anticipoFiltroAplicado === "todos" ? true : anticipoFiltroAplicado === "aplicado" ? a.aplicado : !a.aplicado).length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Sin anticipos registrados</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB HUÉSPEDES ──────────────────────────────────────────────── */}
        {tab === "huespedes" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-400">{huespedes.length} huésped(es) frecuente(s)</p>
              <div className="flex gap-2">
                <input
                  className="px-3 py-1.5 bg-arandu-dark-light border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 w-56"
                  placeholder="Buscar por nombre o doc..."
                  value={huespedesQ}
                  onChange={e => setHuespedesQ(e.target.value)}
                />
                <button onClick={fetchHuespedes} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
            {loadingHuespedes ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
            ) : (
              <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Nombre</th>
                      <th className="px-4 py-3 text-left">Documento</th>
                      <th className="px-4 py-3 text-left">Nacionalidad</th>
                      <th className="px-4 py-3 text-left">Motivo habitual</th>
                      <th className="px-4 py-3 text-center">Visitas</th>
                      <th className="px-4 py-3 text-center">Última visita</th>
                      {canEliminarHuespedes && <th className="px-4 py-3 text-center">Acc.</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {huespedes
                      .filter(h => !huespedesQ || (h.nombre||"").toLowerCase().includes(huespedesQ.toLowerCase()) || (h.nro_doc||"").toLowerCase().includes(huespedesQ.toLowerCase()))
                      .map(h => (
                      <tr key={h.id} className="hover:bg-white/3 transition-colors">
                        <td className="px-4 py-3 font-semibold text-white">{h.nombre}</td>
                        <td className="px-4 py-3 text-slate-400">{h.tipo_doc} {h.nro_doc}</td>
                        <td className="px-4 py-3 text-slate-400">{h.nacionalidad}{h.procedencia ? ` · ${h.procedencia}` : ""}</td>
                        <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{h.motivo_viaje || "—"}</td>
                        <td className="px-4 py-3 text-center text-cyan-400 font-bold">{h.total_visitas || 1}</td>
                        <td className="px-4 py-3 text-center text-xs text-slate-500">{h.ultima_visita ? fmtFecha(h.ultima_visita.slice(0,10)) : "—"}</td>
                        {canEliminarHuespedes && (
                          <td className="px-4 py-3 text-center">
                            <button onClick={() => handleEliminarHuesped(h.id)} className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" title="Eliminar del historial">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {huespedes.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                        Sin huéspedes registrados. Se auto-registran al hacer check-in.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── MODALES ───────────────────────────────────────────────────────── */}

      {/* Modal Nueva / Editar Reserva */}
      {modalReserva !== null && (
        <Modal onClose={() => setModalReserva(null)} title={modalReserva==="nuevo" ? "Nueva Reserva" : `Editar Reserva`} icon={<CalendarDays className="w-5 h-5 text-cyan-400" />} wide>
          <TabErrorBoundary>
          <div className="space-y-4">
            {/* Responsable + fechas */}
            <ClienteSearchSelect
              clientes={clientes}
              value={resForm.responsable_financiero_id}
              onChange={id => setResForm(p => ({ ...p, responsable_financiero_id: id }))}
              onClienteCreado={c => setClientes(cs => {
                const lista = Array.isArray(cs) ? cs : [];
                return lista.some(x => x.id === c.id) ? lista.map(x => x.id === c.id ? c : x) : [c, ...lista];
              })}
              puedeCrear={canCrearCliente}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Fecha de Entrada">
                <DateInput className={INPUT} value={resForm.fecha_entrada} onChange={e => {
                  setResForm(p => ({ ...p, fecha_entrada: e.target.value }));
                  fetchDisponibilidad(e.target.value, resForm.fecha_salida, modalReserva!=="nuevo" ? modalReserva?.id : null);
                }} />
              </Field>
              <Field label="Fecha de Salida">
                <DateInput className={INPUT} value={resForm.fecha_salida} onChange={e => {
                  setResForm(p => ({ ...p, fecha_salida: e.target.value }));
                  fetchDisponibilidad(resForm.fecha_entrada, e.target.value, modalReserva!=="nuevo" ? modalReserva?.id : null);
                }} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Motivo del viaje">
                <input className={INPUT} value={resForm.motivo_viaje} onChange={e => setResForm(p => ({ ...p, motivo_viaje: e.target.value }))} placeholder="Turismo, Negocios..." />
              </Field>
              <Field label="Notas">
                <input className={INPUT} value={resForm.notas} onChange={e => setResForm(p => ({ ...p, notas: e.target.value }))} placeholder="Observaciones..." />
              </Field>
            </div>

            {/* Habitaciones */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                  Habitaciones ({resForm.habitaciones.length})
                  {noches(resForm.fecha_entrada, resForm.fecha_salida) > 0 && (
                    <span className="ml-2 normal-case font-normal">· {noches(resForm.fecha_entrada, resForm.fecha_salida)} noche(s)</span>
                  )}
                </p>
                <div className="relative" ref={dropdownHabsRef}>
                  <button
                    onClick={() => setDropdownHabsOpen(o => !o)}
                    className="flex items-center gap-1 px-2 py-1 bg-cyan-500/15 text-cyan-400 rounded text-xs hover:bg-cyan-500/25 transition-colors"
                    disabled={loadingDisp}
                  >
                    {loadingDisp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Agregar
                  </button>
                  {/* Dropdown de habitaciones disponibles — solo se abre al hacer click */}
                  {dropdownHabsOpen && !loadingDisp && (
                    <div className="absolute right-0 top-full mt-1 z-50 bg-arandu-dark-light border border-white/10 rounded-xl shadow-2xl min-w-48 overflow-hidden">
                      {disponibles.filter(n => !resForm.habitaciones.find(h => h.habitacion_numero === n)).length === 0 ? (
                        <p className="px-3 py-3 text-xs text-slate-500 text-center">
                          {disponibles.length === 0 && resForm.fecha_entrada && resForm.fecha_salida
                            ? "No hay habitaciones disponibles"
                            : "Todas las disponibles ya fueron agregadas"}
                        </p>
                      ) : disponibles
                          .filter(n => !resForm.habitaciones.find(h => h.habitacion_numero === n))
                          .map(n => {
                            const hab = habitaciones.find(h => h.numero === n);
                            return hab ? (
                              <button key={n} onClick={() => { agregarHabitacion(n); setDropdownHabsOpen(false); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 transition-colors">
                                <span className="text-white font-semibold">Hab. {hab.numero}</span>
                                <span className="text-slate-400 ml-2 text-xs">{hab.tipo}</span>
                                <span className="text-emerald-400 ml-2 text-xs">Gs. {fmt(hab.precio_habitacion)}/n</span>
                              </button>
                            ) : null;
                          })}
                    </div>
                  )}
                </div>
              </div>

              {resForm.habitaciones.length === 0 ? (
                <div className="border border-dashed border-white/10 rounded-xl p-6 text-center text-slate-500 text-sm">
                  <BedDouble className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  Seleccioná fechas y luego agregá habitaciones
                </div>
              ) : (
                <div className="space-y-3">
                  {resForm.habitaciones.map(h => {
                    const n = noches(resForm.fecha_entrada, resForm.fecha_salida);
                    const subtotal = h.esquema_cobro === "persona"
                      ? (h._precio_persona||0)*(h.cantidad_personas||1)*n
                      : (h._precio_habitacion||0)*n;
                    return (
                      <div key={h.habitacion_numero} className="bg-arandu-dark border border-white/10 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-white font-bold text-sm">Hab. {h.habitacion_numero}</span>
                            <span className="text-slate-400 text-xs ml-2">{h._tipo}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {n > 0 && <span className="text-emerald-400 text-xs font-mono">Gs. {fmt(subtotal)}</span>}
                            <button onClick={() => quitarHabitacion(h.habitacion_numero)} className="p-1 rounded text-red-500/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                          {/* Esquema de cobro y cantidad */}
                          <div className="grid grid-cols-2 gap-2">
                            <Field label="Esquema de cobro">
                              <select className={INPUT} value={h.esquema_cobro} onChange={e => {
                                const val = e.target.value;
                                setResForm(p => ({
                                  ...p,
                                  habitaciones: p.habitaciones.map(hh =>
                                    hh.habitacion_numero === h.habitacion_numero
                                      ? {
                                          ...hh,
                                          esquema_cobro: val,
                                          huespedes_adicionales: [],
                                          ...(val === "habitacion" ? { cantidad_personas: hh._capacidad || 1 } : { cantidad_personas: 1 }),
                                        }
                                      : hh
                                  ),
                                }));
                              }}>
                                <option value="habitacion">Por Habitación — Gs. {fmt(h._precio_habitacion)}/n</option>
                                <option value="persona">Por Persona — Gs. {fmt(h._precio_persona)}/pers/n</option>
                              </select>
                            </Field>
                            <Field label={`Cant. personas (máx. ${h._capacidad || 20})`}>
                              {(
                                <input type="number" min={1} max={h._capacidad || 20} className={INPUT}
                                  value={h.cantidad_personas}
                                  title={`Capacidad máxima de la habitación: ${h._capacidad || 20}`}
                                  onChange={e => {
                                    const n = Math.min(h._capacidad || 20, Math.max(1, Number(e.target.value) || 1));
                                    setResForm(p => ({
                                      ...p,
                                      habitaciones: p.habitaciones.map(hh =>
                                        hh.habitacion_numero === h.habitacion_numero
                                          ? {
                                              ...hh,
                                              cantidad_personas: n,
                                                        huespedes_adicionales: Array(Math.max(0, n - 1))
                                                .fill(null)
                                                .map((_, i) => (hh.huespedes_adicionales || [])[i] || emptyHuespedAdicional()),
                                            }
                                          : hh
                                      ),
                                    }));
                                  }} />
                              )}
                            </Field>
                          </div>

                          {/* Huésped principal */}
                          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mt-1">
                            {h.cantidad_personas > 1 ? "Huésped 1 (principal)" : "Huésped"}
                          </p>
                          <Field label="Nombre *">
                            <HuespedNombreInput value={h.huesped_nombre} clientes={clientes}
                              onChange={v => updateHuespedHab(h.habitacion_numero, "huesped_nombre", v)}
                              onPick={d => Object.entries(d).forEach(([k, v]) => updateHuespedHab(h.habitacion_numero, k === "nombre" ? "huesped_nombre" : k, v))} />
                          </Field>
                          <div className="grid grid-cols-2 gap-2">
                            <Field label="Tipo Doc.">
                              <select className={INPUT} value={h.tipo_doc} onChange={e => updateHuespedHab(h.habitacion_numero, "tipo_doc", e.target.value)}>
                                {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </Field>
                            <Field label="N° Documento *">
                              <input className={INPUT} value={h.nro_doc||""} onChange={e => updateHuespedHab(h.habitacion_numero, "nro_doc", e.target.value)} placeholder="CI o Pasaporte" />
                            </Field>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Field label="Celular *">
                              <input className={INPUT} value={h.celular||""} onChange={e => updateHuespedHab(h.habitacion_numero, "celular", e.target.value)} placeholder="Número de celular" />
                            </Field>
                            <Field label="Nacionalidad">
                              <input className={INPUT} value={h.nacionalidad} onChange={e => updateHuespedHab(h.habitacion_numero, "nacionalidad", e.target.value)} placeholder="Paraguaya" />
                            </Field>
                          </div>
                          <Field label="Procedencia">
                            <input className={INPUT} value={h.procedencia||""} onChange={e => updateHuespedHab(h.habitacion_numero, "procedencia", e.target.value)} placeholder="Ciudad de origen" />
                          </Field>

                          {/* Huéspedes adicionales — formulario completo igual que el principal */}
                          {h.cantidad_personas > 1 && Array.from({ length: h.cantidad_personas - 1 }).map((_, idx) => {
                            const ha = (h.huespedes_adicionales || [])[idx] || emptyHuespedAdicional();
                            const updateHaMulti = (obj) => {
                              const nuevos = Array.from({ length: h.cantidad_personas - 1 })
                                .map((_, i) => (h.huespedes_adicionales || [])[i] || emptyHuespedAdicional());
                              nuevos[idx] = { ...nuevos[idx], ...obj };
                              updateHuespedHab(h.habitacion_numero, "huespedes_adicionales", nuevos);
                            };
                            const updateHa = (field, val) => {
                              const nuevos = Array.from({ length: h.cantidad_personas - 1 })
                                .map((_, i) => (h.huespedes_adicionales || [])[i] || emptyHuespedAdicional());
                              nuevos[idx] = { ...nuevos[idx], [field]: val };
                              updateHuespedHab(h.habitacion_numero, "huespedes_adicionales", nuevos);
                            };
                            return (
                              <div key={idx} className="mt-2 pt-2 border-t border-white/5">
                                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-2">
                                  Huésped {idx + 2}
                                </p>
                                <Field label="Nombre *">
                                  <HuespedNombreInput value={ha.nombre||""} clientes={clientes}
                                    onChange={v => updateHa("nombre", v)} onPick={d => updateHaMulti(d)} />
                                </Field>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <Field label="Tipo Doc.">
                                    <select className={INPUT} value={ha.tipo_doc||"C.I.P."} onChange={e => updateHa("tipo_doc", e.target.value)}>
                                      {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                  </Field>
                                  <Field label="N° Documento *">
                                    <input className={INPUT} value={ha.nro_doc||""} placeholder="CI o Pasaporte" onChange={e => updateHa("nro_doc", e.target.value)} />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <Field label="Celular *">
                                    <input className={INPUT} value={ha.celular||""} placeholder="Número de celular" onChange={e => updateHa("celular", e.target.value)} />
                                  </Field>
                                  <Field label="Nacionalidad">
                                    <input className={INPUT} value={ha.nacionalidad||"Paraguaya"} placeholder="Paraguaya" onChange={e => updateHa("nacionalidad", e.target.value)} />
                                  </Field>
                                </div>
                                <div className="mt-2">
                                  <Field label="Procedencia">
                                    <input className={INPUT} value={ha.procedencia||""} placeholder="Ciudad de origen" onChange={e => updateHa("procedencia", e.target.value)} />
                                  </Field>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Total */}
            {totalEstimado > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex justify-between items-center">
                <span className="text-slate-400 text-sm">{resForm.habitaciones.length} hab. · {noches(resForm.fecha_entrada, resForm.fecha_salida)} noche(s) — Total estimado:</span>
                <span className="text-emerald-400 font-bold text-lg">Gs. {fmt(totalEstimado)}</span>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalReserva(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleGuardarReserva} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" />{modalReserva==="nuevo" ? "Crear Reserva" : "Guardar Cambios"}</>}
              </button>
            </div>
          </div>
          </TabErrorBoundary>
        </Modal>
      )}

      {/* Modal Extracto */}
      {modalExtracto && (
        <Modal onClose={() => setModalExtracto(null)} title="Cuenta de la Reserva" icon={<Receipt className="w-5 h-5 text-slate-400" />} wide>
          <div className="space-y-4">
            {/* Header info */}
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-arandu-dark border border-white/10 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Estadía</p>
                <p className="text-white font-bold">{fmtFecha(modalExtracto.fecha_entrada)} → {fmtFecha(modalExtracto.fecha_salida)}</p>
                <p className="text-slate-400 text-xs">{modalExtracto.cantidad_dias} noche(s)</p>
              </div>
              <div className="bg-arandu-dark border border-white/10 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Responsable</p>
                <p className="text-white font-semibold truncate">{modalExtracto.responsable_nombre || "—"}</p>
              </div>
              <div className="bg-arandu-dark border border-white/10 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Estado</p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${RES_STATUS_COLOR[modalExtracto.status]}`}>{RES_STATUS_LABEL[modalExtracto.status]}</span>
              </div>
            </div>

            {/* Habitaciones */}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Hospedaje por habitación</p>
              <div className="bg-arandu-dark border border-white/10 rounded-lg divide-y divide-white/5">
                {(modalExtracto.habitaciones||[]).map((h, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2.5 text-sm">
                    <div>
                      <span className="text-white font-semibold">Hab. {h.habitacion_numero}</span>
                      <span className="text-slate-400 text-xs ml-2">{h.tipo_habitacion}</span>
                      <span className="text-slate-500 text-xs ml-2">— {h.huesped_nombre}</span>
                    </div>
                    <span className="text-slate-300 font-mono text-xs">Gs. {fmt(h.valor_estadia)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gastos */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Consumos</p>
                {canGasto && ["pendiente","en_curso"].includes(modalExtracto.status) && (
                  <button onClick={() => { setModalGasto(modalExtracto.reserva_id); setGastoForm({ habitacion_numero: modalExtracto.habitaciones?.[0]?.habitacion_numero||"", producto_id:"", descripcion:"", cantidad:1, precio_unitario:0, origen:"Frigobar" }); }}
                    className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Agregar
                  </button>
                )}
              </div>
              <div className="bg-arandu-dark border border-white/10 rounded-lg overflow-y-auto max-h-60">
                {(modalExtracto.gastos||[]).length === 0 ? (
                  <p className="text-center text-slate-500 text-sm py-4">Sin consumos</p>
                ) : (() => {
                  // Agrupar gastos por habitacion_numero
                  const grupos = {};
                  (modalExtracto.gastos||[]).forEach((g, i) => {
                    const key = g.habitacion_numero || "__general__";
                    if (!grupos[key]) grupos[key] = [];
                    grupos[key].push({ ...g, _idx: i });
                  });
                  return Object.entries(grupos).map(([hab, items]) => (
                    <div key={hab}>
                      <div className="px-3 py-1.5 bg-white/5 border-b border-white/5">
                        <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                          {hab === "__general__" ? "General" : `Hab. ${hab}`}
                        </span>
                      </div>
                      {items.map(g => (
                        <div key={g._idx} className="flex items-center justify-between px-3 py-2 text-sm border-b border-white/5 last:border-0">
                          <div>
                            <span className="text-xs text-slate-500 mr-2">[{g.origen}]</span>
                            <span className="text-slate-300">{g.descripcion}</span>
                            <span className="text-xs text-slate-500 ml-2">×{g.cantidad}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-mono text-xs">Gs. {fmt(g.cantidad*g.precio_unitario)}</span>
                            {canGasto && <button onClick={() => eliminarGasto(g._idx)} className="p-1 rounded text-red-500/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"><X className="w-3 h-3" /></button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Anticipos */}
            {(modalExtracto.pagos_adelantados||[]).length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Anticipos registrados</p>
                  {canAnticipo && ["pendiente","en_curso"].includes(modalExtracto.status) && (
                    <button onClick={() => { setModalAnticipo(modalExtracto.reserva_id); setAnticipo({ monto: 0, metodo: "efectivo", cuenta_id: cuentas.find(c => (c.moneda||"PYG") === "PYG")?.id || "", notas: "" }); }}
                      className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Agregar
                    </button>
                  )}
                </div>
                <div className="bg-arandu-dark border border-white/10 rounded-lg overflow-hidden">
                  {(modalExtracto.pagos_adelantados||[]).map((p, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm border-b border-white/5 last:border-0">
                      <div>
                        <span className="text-violet-300 font-semibold">Anticipo</span>
                        <span className="text-xs text-slate-500 ml-2">{p.fecha} · {p.metodo}</span>
                        {p.notas && <span className="text-xs text-slate-500 ml-2">— {p.notas}</span>}
                      </div>
                      <span className="text-violet-400 font-mono text-sm">Gs. {fmt(p.monto)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(modalExtracto.pagos_adelantados||[]).length === 0 && canAnticipo && ["pendiente","en_curso"].includes(modalExtracto.status) && (
              <button onClick={() => { setModalAnticipo(modalExtracto.reserva_id); setAnticipo({ monto: 0, metodo: "efectivo", cuenta_id: cuentas.find(c => (c.moneda||"PYG") === "PYG")?.id || "", notas: "" }); }}
                className="w-full py-2 text-sm text-violet-400 border border-violet-500/20 rounded-lg hover:bg-violet-500/10 transition-colors flex items-center justify-center gap-2">
                <Plus className="w-3.5 h-3.5" /> Registrar anticipo
              </button>
            )}

            {/* Total */}
            <div className="bg-arandu-dark border border-white/10 rounded-lg p-4">
              <div className="flex justify-between text-sm text-slate-400 mb-1">
                <span>Subtotal hospedaje</span><span className="font-mono">Gs. {fmt(modalExtracto.valor_total_hospedaje)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>Subtotal consumos</span><span className="font-mono">Gs. {fmt(modalExtracto.total_gastos)}</span>
              </div>
              {(modalExtracto.total_anticipos||0) > 0 && (
                <div className="flex justify-between text-sm text-violet-400 mb-2">
                  <span>Anticipos cobrados</span><span className="font-mono">Gs. {fmt(modalExtracto.total_anticipos)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold">
                <span className="text-white text-lg">TOTAL GENERAL</span>
                <span className="text-emerald-400 text-xl font-mono">Gs. {fmt(modalExtracto.total_general)}</span>
              </div>
              <div className="text-xs text-slate-500 text-right mt-1">IVA 10% incluido: Gs. {fmt(modalExtracto.iva_10)}</div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={() => imprimirExtracto(modalExtracto)} className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm hover:bg-blue-500/20 transition-colors">
                <Printer className="w-4 h-4" /> Extracto A4
              </button>
              <button onClick={() => imprimirExtractoTicket(modalExtracto)} className="flex items-center gap-2 px-3 py-2 bg-violet-500/10 text-violet-400 rounded-lg text-sm hover:bg-violet-500/20 transition-colors">
                🧾 Ticket
              </button>
              <button onClick={() => imprimirFichasSenatur(modalExtracto)} className="flex items-center gap-2 px-3 py-2 bg-indigo-500/10 text-indigo-400 rounded-lg text-sm hover:bg-indigo-500/20 transition-colors">
                <FileText className="w-4 h-4" /> Fichas SENATUR ({(modalExtracto.habitaciones||[]).reduce((a,h) => a + (h.cantidad_personas||1), 0)})
              </button>
              {canCheckout && modalExtracto.status === "en_curso" && (
                <button onClick={() => handleCheckout(modalExtracto.reserva_id)} className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm hover:bg-emerald-500/30 transition-colors font-semibold ml-auto">
                  <LogOut className="w-4 h-4" /> Procesar Check-Out
                </button>
              )}
              {canCheckout && modalExtracto.status === "pendiente_factura" && (
                <button onClick={() => openFacturar(modalExtracto.reserva_id)} className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-300 rounded-lg text-sm hover:bg-violet-500/30 transition-colors font-semibold ml-auto">
                  <Receipt className="w-4 h-4" /> Crear factura
                </button>
              )}
              {canCheckin && modalExtracto.status === "pendiente" && (
                <button onClick={() => handleCheckin(modalExtracto.reserva_id)} className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors font-semibold ml-auto">
                  <LogIn className="w-4 h-4" /> Confirmar Check-In
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Gasto */}
      {modalGasto && (
        <Modal onClose={() => setModalGasto(null)} title="Registrar Consumo" icon={<ShoppingCart className="w-5 h-5 text-rose-400" />}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Origen">
                <select className={INPUT} value={gastoForm.origen} onChange={e => setGastoForm(p => ({ ...p, origen: e.target.value }))}>
                  {ORIGENES_GASTO.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Habitación">
                <select className={INPUT} value={gastoForm.habitacion_numero} onChange={e => setGastoForm(p => ({ ...p, habitacion_numero: e.target.value }))}>
                  <option value="">— General —</option>
                  {(reservas.find(r => r.id === modalGasto)?.habitaciones || []).map(h => (
                    <option key={h.habitacion_numero} value={h.habitacion_numero}>Hab. {h.habitacion_numero}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Producto del catálogo">
              <ProductoSearchSelect productos={productos} value={gastoForm.producto_id}
                onChange={(id, prod) => {
                  if (prod) setGastoForm(p => ({ ...p, producto_id: id, descripcion: prod.nombre, precio_unitario: prod.precio_venta || 0 }));
                  else setGastoForm(p => ({ ...p, producto_id: "", descripcion: "", precio_unitario: 0 }));
                }} placeholder="Buscar producto..." />
            </Field>
            <Field label="Descripción">
              <input className={INPUT} value={gastoForm.descripcion} onChange={e => setGastoForm(p => ({ ...p, descripcion: e.target.value }))} placeholder="Descripción del consumo" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Cantidad">
                <input type="number" min={1} className={INPUT} value={gastoForm.cantidad} onChange={e => setGastoForm(p => ({ ...p, cantidad: e.target.value }))} />
              </Field>
              <Field label="Precio Unitario (Gs.)">
                <input type="number" min={0} className={INPUT} value={gastoForm.precio_unitario} onChange={e => setGastoForm(p => ({ ...p, precio_unitario: e.target.value }))} />
              </Field>
            </div>
            {gastoForm.cantidad > 0 && gastoForm.precio_unitario > 0 && (
              <div className="text-right text-sm text-slate-400">Subtotal: <span className="text-white font-bold">Gs. {fmt(Number(gastoForm.cantidad)*Number(gastoForm.precio_unitario))}</span></div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalGasto(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleGasto} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-rose-500/20 text-rose-400 rounded-lg text-sm hover:bg-rose-500/30 transition-colors font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShoppingCart className="w-4 h-4" /> Registrar</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Checkout → Factura */}
      {modalCheckout && (
        <Modal onClose={() => setModalCheckout(null)} title="Crear Factura de Hospedaje" icon={<Receipt className="w-5 h-5 text-cyan-400" />} wide>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Ítems a facturar</p>
              <div className="bg-arandu-dark border border-white/10 rounded-lg divide-y divide-white/5 max-h-48 overflow-y-auto">
                {modalCheckout.conceptos.map((c, i) => (
                  <div key={i} className="flex justify-between px-3 py-2 text-sm">
                    <span className="text-slate-300">{c.descripcion}{c.cantidad>1?` ×${c.cantidad}`:""}</span>
                    <span className="text-slate-400 font-mono">Gs. {fmt(c.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between px-3 pt-2 font-bold text-sm">
                <span className="text-slate-300">Total</span>
                <span className="text-emerald-400 font-mono">Gs. {fmt(modalCheckout.total)}</span>
              </div>
            </div>
            {/* Anticipos pre-pagados */}
            {(modalCheckout.pagos_adelantados || []).filter(p => p.anticipo_id).length > 0 && (
              <div className="bg-violet-500/10 border border-violet-500/25 rounded-lg p-3 space-y-1">
                <p className="text-xs text-violet-400 font-semibold uppercase tracking-wider mb-2">Anticipos registrados — se aplicarán automáticamente</p>
                {(modalCheckout.pagos_adelantados || []).filter(p => p.anticipo_id).map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-300">{fmtFecha(p.fecha)} · {p.metodo}{p.notas ? ` · ${p.notas}` : ""}</span>
                    <span className="text-violet-300 font-mono">Gs. {fmt(p.monto)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold border-t border-violet-500/20 pt-1 mt-1">
                  <span className="text-slate-300">Saldo pendiente</span>
                  <span className="text-amber-400 font-mono">Gs. {fmt(modalCheckout.saldo_pendiente ?? modalCheckout.total)}</span>
                </div>
              </div>
            )}
            {modalCheckout.cliente.razon_social && (
              <div className="bg-arandu-dark border border-white/10 rounded-lg p-3 text-sm">
                <p className="text-xs text-slate-500 mb-1">Cliente</p>
                <p className="text-white font-semibold">{modalCheckout.cliente.razon_social}</p>
                {modalCheckout.cliente.ruc && <p className="text-slate-400 text-xs">RUC: {modalCheckout.cliente.ruc}</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="N° Factura *">
                {tbConfig && (tbConfig.puntos_expedicion || []).length > 1 && (
                  <select className={INPUT + " mb-2"} value={coForm.punto_expedicion || ""}
                    disabled={!!tbConfig.punto_bloqueado}
                    onChange={e => setCoForm(p => ({ ...p, punto_expedicion: e.target.value, numero: "" }))}>
                    {tbConfig.puntos_expedicion.map(pt => (
                      <option key={pt.codigo} value={pt.codigo}>Punto {pt.codigo}{pt.descripcion ? ` — ${pt.descripcion}` : ""}</option>
                    ))}
                  </select>
                )}
                {numeroAuto ? (
                  <div className="flex gap-2">
                    <input className={INPUT + " flex-1"} value={coForm.numero} readOnly placeholder="Número auto-generado" />
                    <button type="button" disabled={loadingNumero || !timbradoOk}
                      onClick={() => generarNumero(coForm.punto_expedicion)}
                      className="px-3 py-2 bg-cyan-600 text-white text-xs rounded-lg hover:bg-cyan-700 disabled:opacity-50 whitespace-nowrap flex items-center gap-1">
                      {loadingNumero ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Generar N°"}
                    </button>
                  </div>
                ) : (
                  <input className={INPUT} value={coForm.numero} onChange={e => setCoForm(p => ({ ...p, numero: e.target.value }))} placeholder="001-001-0000001" />
                )}
                {tbConfig && puntoSel && !timbradoOk && (
                  <p className="text-[11px] text-red-400 mt-1">Timbrado vencido o sin configurar en este punto.</p>
                )}
              </Field>
              <Field label="Fecha"><DateInput className={INPUT} value={coForm.fecha} onChange={e => setCoForm(p => ({ ...p, fecha: e.target.value }))} /></Field>
              <Field label="Forma de pago">
                <select className={INPUT} value={coForm.forma_pago} onChange={e => setCoForm(p => ({ ...p, forma_pago: e.target.value }))}>
                  <option value="contado">Contado</option><option value="credito">Crédito</option>
                  <option value="transferencia">Transferencia</option><option value="tarjeta">Tarjeta</option>
                </select>
              </Field>
              <Field label="Cuenta bancaria">
                <select className={INPUT} value={coForm.cuenta_id} onChange={e => setCoForm(p => ({ ...p, cuenta_id: e.target.value }))}>
                  <option value="">— Sin cuenta —</option>
                  <CuentaOptions cuentas={cuentas} />
                </select>
              </Field>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalCheckout(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={crearFacturaHotel} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Crear Factura</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Anticipo */}
      {modalAnticipo && (
        <Modal onClose={() => setModalAnticipo(null)} title="Registrar Anticipo" icon={<Receipt className="w-5 h-5 text-violet-400" />}>
          <div className="space-y-4">
            <p className="text-xs text-slate-400">Registrá un pago adelantado. La factura final se emitirá por el monto total al hacer checkout.</p>
            <Field label="Monto (Gs.) *">
              <input type="number" min={0} className={INPUT} value={anticipoForm.monto}
                onChange={e => setAnticipo(p => ({ ...p, monto: Number(e.target.value) }))} placeholder="0" />
            </Field>
            <Field label="Método de pago">
              <select className={INPUT} value={anticipoForm.metodo} onChange={e => setAnticipo(p => ({ ...p, metodo: e.target.value }))}>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="cheque">Cheque</option>
              </select>
            </Field>
            <Field label="Cuenta bancaria">
              <select className={INPUT} value={anticipoForm.cuenta_id} onChange={e => setAnticipo(p => ({ ...p, cuenta_id: e.target.value }))}>
                <option value="">— Sin cuenta —</option>
                <CuentaOptions cuentas={cuentas} />
              </select>
            </Field>
            <Field label="Notas">
              <input className={INPUT} value={anticipoForm.notas}
                onChange={e => setAnticipo(p => ({ ...p, notas: e.target.value }))} placeholder="Ej: Anticipo 50%" />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalAnticipo(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleRegistrarAnticipo} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-lg text-sm hover:bg-violet-500/30 transition-colors font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Registrar</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal CRUD Habitación */}
      {modalHab !== null && (
        <Modal onClose={() => setModalHab(null)} title={habForm.id ? `Editar Hab. N° ${habForm.numero}` : "Nueva Habitación"} icon={<BedDouble className="w-5 h-5 text-cyan-400" />}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Número *">
                <input className={INPUT} value={habForm.numero||""} onChange={e => setHabForm(p => ({ ...p, numero: e.target.value }))} placeholder="101" disabled={!!habForm.id} />
              </Field>
              <Field label="Tipo / Categoría *">
                <select className={INPUT} value={habForm.tipo||""} onChange={e => {
                  const tipo = e.target.value;
                  const [ph,pp] = PRECIOS_HAB[tipo] || [habForm.precio_habitacion, habForm.precio_persona];
                  const cap = CAP_HAB[tipo] || habForm.capacidad || 2;
                  const [ci, co] = HORARIO_HAB[tipo] || [habForm.horario_checkin || "14:00", habForm.horario_checkout || "10:00"];
                  setHabForm(p => ({ ...p, tipo, precio_habitacion: ph, precio_persona: pp, capacidad: cap, horario_checkin: ci, horario_checkout: co }));
                }}>
                  {TIPOS_HAB.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Precio por Habitación (Gs.)">
                <input type="number" min={0} className={INPUT} value={habForm.precio_habitacion||0} onChange={e => setHabForm(p => ({ ...p, precio_habitacion: Number(e.target.value) }))} />
              </Field>
              <Field label="Precio por Persona (Gs.)">
                <input type="number" min={0} className={INPUT} value={habForm.precio_persona||0} onChange={e => setHabForm(p => ({ ...p, precio_persona: Number(e.target.value) }))} />
              </Field>
              <Field label="Régimen por defecto">
                <select className={INPUT} value={habForm.regimen_cobro||"habitacion"} onChange={e => setHabForm(p => ({ ...p, regimen_cobro: e.target.value }))}>
                  <option value="habitacion">Por Habitación</option>
                  <option value="persona">Por Persona</option>
                </select>
              </Field>
              <Field label="Capacidad máx. (personas)">
                <input type="number" min={1} max={20} className={INPUT} value={habForm.capacidad ?? 2} onChange={e => setHabForm(p => ({ ...p, capacidad: Number(e.target.value) }))} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Check-In"><input type="time" className={INPUT} value={habForm.horario_checkin||"14:00"} onChange={e => setHabForm(p => ({ ...p, horario_checkin: e.target.value }))} /></Field>
                <Field label="Check-Out"><input type="time" className={INPUT} value={habForm.horario_checkout||"10:00"} onChange={e => setHabForm(p => ({ ...p, horario_checkout: e.target.value }))} /></Field>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalHab(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleSaveHab} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Sub-componentes ───────────────────────────────────────────────────────────

function PanelCard({ hab, onCheckin, onGasto, onExtracto, onVerReserva, onAnticipo, onFacturar }) {
  return (
    <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden relative">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${STATUS_LINE[hab.status]}`} />
      <div className="p-4 pl-5">
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="text-lg font-bold text-white leading-none">Hab. {hab.numero}</p>
            <p className="text-xs text-slate-400 mt-0.5">{hab.tipo}</p>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLOR[hab.status]}`}>{STATUS_LABEL[hab.status]}</span>
        </div>

        {hab.status === "disponible" && (
          <>
            <p className="text-emerald-400 font-bold text-sm">Gs. {Number(hab.precio_habitacion).toLocaleString("es-PY")} <span className="text-slate-500 font-normal text-xs">/ noche</span></p>
            <p className="text-cyan-400 font-bold text-sm">Gs. {Number(hab.precio_persona).toLocaleString("es-PY")} <span className="text-slate-500 font-normal text-xs">/ persona</span></p>
            <p className="text-xs text-slate-500 mt-1">In: {hab.horario_checkin} hs. · Out: {hab.horario_checkout} hs.</p>
          </>
        )}

        {hab.status === "reservado" && (
          <>
            <p className="text-sm text-amber-300 font-semibold truncate mb-0.5">{hab.huesped_nombre_card || "Reservado"}</p>
            <p className="text-xs text-slate-500 mb-1">Entrada: {hab.fecha_entrada_card ? fmtFecha(hab.fecha_entrada_card) : "—"}</p>
            <p className="text-xs text-slate-500 mb-3">Salida: {hab.fecha_salida_card ? fmtFecha(hab.fecha_salida_card) : "—"}</p>
            <div className="flex gap-2">
              {onCheckin && (
                <button onClick={onCheckin} className="flex-1 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 text-xs font-semibold hover:bg-emerald-500/25 transition-colors flex items-center justify-center gap-1">
                  <LogIn className="w-3 h-3" /> Check-In
                </button>
              )}
              {onVerReserva && (
                <button onClick={onVerReserva} className="flex-1 py-1.5 rounded-lg bg-slate-500/15 text-slate-400 text-xs font-semibold hover:bg-slate-500/25 transition-colors flex items-center justify-center gap-1">
                  <Receipt className="w-3 h-3" /> Ver
                </button>
              )}
            </div>
          </>
        )}

        {hab.status === "por_facturar" && (
          <>
            <p className="text-sm text-violet-300 font-semibold truncate mb-0.5">{hab.huesped_nombre_card || "Check-out realizado"}</p>
            <p className="text-xs text-slate-500 mb-3">Check-out hecho · falta emitir la factura</p>
            <div className="flex gap-2">
              {onFacturar && (
                <button onClick={onFacturar} className="flex-1 py-1.5 rounded-lg bg-violet-500/20 text-violet-300 text-xs font-semibold hover:bg-violet-500/30 transition-colors flex items-center justify-center gap-1">
                  <Receipt className="w-3 h-3" /> Crear factura
                </button>
              )}
              {onVerReserva && (
                <button onClick={onVerReserva} className="flex-1 py-1.5 rounded-lg bg-slate-500/15 text-slate-400 text-xs font-semibold hover:bg-slate-500/25 transition-colors flex items-center justify-center gap-1">
                  <Receipt className="w-3 h-3" /> Ver
                </button>
              )}
            </div>
          </>
        )}

        {hab.status === "ocupado" && (
          <>
            <p className="text-sm text-white font-semibold truncate mb-0.5">{hab.huesped_nombre_card || "Huésped activo"}</p>
            <p className="text-xs text-slate-500 mb-1">Ingresó: {hab.fecha_entrada_card ? fmtFecha(hab.fecha_entrada_card) : "—"}</p>
            <p className="text-xs text-slate-500 mb-3">Out: {hab.horario_checkout} hs.</p>
            <div className="flex gap-2 flex-wrap">
              {onGasto && (
                <button onClick={onGasto} className="flex-1 py-1.5 rounded-lg bg-rose-500/15 text-rose-400 text-xs font-semibold hover:bg-rose-500/25 transition-colors flex items-center justify-center gap-1">
                  <ShoppingCart className="w-3 h-3" /> + Consumo
                </button>
              )}
              {onExtracto && (
                <button onClick={onExtracto} className="flex-1 py-1.5 rounded-lg bg-slate-500/15 text-slate-400 text-xs font-semibold hover:bg-slate-500/25 transition-colors flex items-center justify-center gap-1">
                  <Receipt className="w-3 h-3" /> Cuenta
                </button>
              )}
              {onAnticipo && (
                <button onClick={onAnticipo} className="flex-1 py-1.5 rounded-lg bg-violet-500/15 text-violet-400 text-xs font-semibold hover:bg-violet-500/25 transition-colors flex items-center justify-center gap-1">
                  💳 Anticipo
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReservaRow({ res, canCheckout, onFacturar, canCancelarReserva, canEditarReserva, canCheckin, canExtracto, canGasto, canAnticipo, onEditar, onCheckin, onExtracto, onCancelar, onGasto, onAnticipo }) {
  const [expanded, setExpanded] = useState(false);
  // Guard: si res no es un objeto válido, no renderizar nada
  if (!res || typeof res !== "object") return null;
  const status   = res.status ?? "pendiente";
  const habs     = Array.isArray(res.habitaciones) ? res.habitaciones.filter(Boolean) : [];
  const statusColor = RES_STATUS_COLOR[status] ?? "bg-slate-500/15 text-slate-400";
  const statusLabel = RES_STATUS_LABEL[status]  ?? status;
  return (
    <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
            <span className="text-white text-sm font-semibold">{fmtFecha(res.fecha_entrada)} → {fmtFecha(res.fecha_salida)}</span>
            <span className="text-slate-500 text-xs">{res.cantidad_dias || "?"} noche(s)</span>
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-slate-400 text-xs">{res.responsable_nombre || "Sin responsable"}</span>
            <span className="text-slate-500 text-xs">·</span>
            <span className="text-slate-400 text-xs">{habs.length} hab: {habs.map(h=>`N°${h.habitacion_numero}`).join(", ")}</span>
            <span className="text-emerald-400 text-xs font-mono">Gs. {(Number(res.total_general)||0).toLocaleString("es-PY")}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {status === "pendiente" && canCheckin && (
            <button onClick={onCheckin} className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors" title="Check-In"><LogIn className="w-4 h-4" /></button>
          )}
          {status === "en_curso" && onGasto && (
            <button onClick={onGasto} className="p-1.5 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors" title="Agregar consumo"><ShoppingCart className="w-4 h-4" /></button>
          )}
          {status === "en_curso" && canAnticipo && onAnticipo && (
            <button onClick={onAnticipo} className="p-1.5 rounded-lg bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 transition-colors" title="Registrar anticipo"><Receipt className="w-4 h-4" /></button>
          )}
          {status === "pendiente_factura" && canCheckout && onFacturar && (
            <button onClick={onFacturar} className="px-2.5 py-1.5 rounded-lg bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 transition-colors text-xs font-semibold flex items-center gap-1" title="Crear factura"><Receipt className="w-4 h-4" /> Facturar</button>
          )}
          {canExtracto && ["pendiente","en_curso","pendiente_factura"].includes(status) && (
            <button onClick={onExtracto} className="p-1.5 rounded-lg bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 transition-colors" title="Ver cuenta"><Receipt className="w-4 h-4" /></button>
          )}
          {status === "pendiente" && canEditarReserva && (
            <button onClick={onEditar} className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors" title="Editar"><Edit2 className="w-4 h-4" /></button>
          )}
          {status === "pendiente" && canCancelarReserva && (
            <button onClick={onCancelar} className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" title="Cancelar reserva"><XCircle className="w-4 h-4" /></button>
          )}
          <AuditoriaRegistroBtn registro={res} />
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-500 ml-1" /> : <ChevronDown className="w-4 h-4 text-slate-500 ml-1" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-white/5 px-4 py-3 bg-white/2">
          <div className="grid gap-2">
            {habs.map((h, i) => (
              <div key={i} className="flex items-start justify-between text-sm">
                <div>
                  <span className="text-white font-semibold">Hab. {h.habitacion_numero}</span>
                  <span className="text-slate-400 text-xs ml-2">{h.tipo_habitacion}</span>
                  <span className="text-white ml-2">— {h.huesped_nombre}</span>
                  <div className="text-slate-500 text-xs mt-0.5">{h.tipo_doc} {h.nro_doc} · {h.nacionalidad}{h.procedencia ? ` · ${h.procedencia}` : ""}</div>
                </div>
                <span className="text-slate-300 font-mono text-xs">Gs. {(Number(h.valor_estadia)||0).toLocaleString("es-PY")}</span>
              </div>
            ))}
          </div>
          {res.motivo_viaje && <p className="text-xs text-slate-500 mt-2">Motivo: {res.motivo_viaje}</p>}
        </div>
      )}
    </div>
  );
}

function HistoricoTab({ eqs, headers, canExtracto, onExtracto }) {
  const now = new Date();
  const hoyH = () => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
  const prevDiaH = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() - 1); return dt.toISOString().slice(0, 10); };
  const nextDiaH = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() + 1); return dt.toISOString().slice(0, 10); };
  const diaLabelH = (d) => { if (!d) return ""; return new Date(d + "T12:00:00").toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short" }); };

  const [q,        setQ]        = useState("");
  const [periodo,  setPeriodo]  = useState("mes");
  const [mes,      setMes]      = useState(now.getMonth() + 1);
  const [anio,     setAnio]     = useState(now.getFullYear());
  const [periodoDia, setPeriodoDia] = useState(hoyH);
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(false);

  const MESES_CORTO = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("periodo", periodo);
      if (periodo === "dia")  { params.set("dia", periodoDia); }
      if (periodo === "mes")  { params.set("mes", mes); params.set("anio", anio); }
      if (periodo === "anio") { params.set("anio", anio); }
      if (q.trim()) params.set("q", q.trim());
      const sep = eqs ? "&" : "?";
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/hotel/historial${eqs}${sep}${params.toString()}`;
      const res = await fetch(url, { headers });
      setData(res.ok ? await res.json() : []);
    } finally {
      setLoading(false);
    }
  }, [periodo, mes, anio, periodoDia, eqs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // búsqueda local adicional (instantánea mientras escribe)
  const filtrado = q.trim()
    ? data.filter(r => {
        const ql = q.toLowerCase();
        return (
          (r.responsable_nombre||"").toLowerCase().includes(ql) ||
          (r.responsable_ruc||"").toLowerCase().includes(ql) ||
          (r.habitaciones||[]).some(h =>
            (h.huesped_nombre||"").toLowerCase().includes(ql) ||
            String(h.habitacion_numero||"").toLowerCase().includes(ql) ||
            (h.huespedes_adicionales||[]).some(ha => typeof ha === "string"
              ? (ha||"").toLowerCase().includes(ql)
              : ((ha.nombre||"").toLowerCase().includes(ql) || (ha.nro_doc||"").toLowerCase().includes(ql) || (ha.celular||"").toLowerCase().includes(ql))
            )
          ) ||
          (r.motivo_viaje||"").toLowerCase().includes(ql) ||
          String(r.total_general||0).includes(ql)
        );
      })
    : data;

  const totalMonto = filtrado.reduce((s, r) => s + (Number(r.total_general) || 0), 0);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Búsqueda */}
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/60"
            placeholder="Buscar por huésped, RUC, habitación, motivo..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        {/* Período */}
        <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg p-1">
          {[["todo","Todo"],["dia","Día"],["mes","Mes"],["anio","Año"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setPeriodo(val)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${periodo === val ? "bg-cyan-500/20 text-cyan-400" : "text-slate-500 hover:text-slate-300"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {/* Navegación día */}
        {periodo === "dia" && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
              <button onClick={() => setPeriodoDia(prevDiaH(periodoDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-white text-sm font-medium min-w-[110px] text-center px-1 capitalize">{diaLabelH(periodoDia)}</span>
              <button onClick={() => setPeriodoDia(nextDiaH(periodoDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            {periodoDia !== hoyH() && (
              <button onClick={() => setPeriodoDia(hoyH())} className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors">Hoy</button>
            )}
          </div>
        )}
        {/* Navegación mes */}
        {periodo === "mes" && (
          <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
            <button onClick={() => { if (mes === 1) { setMes(12); setAnio(a => a - 1); } else setMes(m => m - 1); }} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white text-sm font-medium min-w-[90px] text-center px-1">{MESES_CORTO[mes - 1]} {anio}</span>
            <button onClick={() => { if (mes === 12) { setMes(1); setAnio(a => a + 1); } else setMes(m => m + 1); }} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Navegación año */}
        {periodo === "anio" && (
          <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
            <button onClick={() => setAnio(a => a - 1)} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white text-sm font-medium min-w-[50px] text-center px-1">{anio}</span>
            <button onClick={() => setAnio(a => a + 1)} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 bg-white/5 text-slate-400 rounded-lg text-sm hover:bg-white/10 transition-colors">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Stats rápidas */}
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span>{filtrado.length} reserva(s)</span>
        <span>·</span>
        <span>Total: <span className="text-emerald-400 font-mono">Gs. {totalMonto.toLocaleString("es-PY")}</span></span>
        {filtrado.filter(r => r.status === "finalizada").length > 0 && <>
          <span>·</span>
          <span>{filtrado.filter(r => r.status === "finalizada").length} finalizadas</span>
        </>}
        {filtrado.filter(r => r.status === "cancelada").length > 0 && <>
          <span>·</span>
          <span>{filtrado.filter(r => r.status === "cancelada").length} canceladas</span>
        </>}
      </div>

      {/* Tabla */}
      <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Estado</th>
              <th className="px-4 py-3 text-left">Entrada</th>
              <th className="px-4 py-3 text-left">Salida</th>
              <th className="px-4 py-3 text-left">Habitación(es)</th>
              <th className="px-4 py-3 text-left">Huésped principal</th>
              <th className="px-4 py-3 text-left">Responsable / RUC</th>
              <th className="px-4 py-3 text-right">Total</th>
              {canExtracto && <th className="px-4 py-3 text-center">Cuenta</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtrado.map(r => {
              const habs = r.habitaciones || [];
              const primerHuesped = habs[0]?.huesped_nombre || "—";
              return (
                <tr key={r.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${RES_STATUS_COLOR[r.status]}`}>
                      {RES_STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{fmtFecha(r.fecha_entrada)}</td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{fmtFecha(r.fecha_salida)}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {habs.map(h => `N°${h.habitacion_numero}`).join(", ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs max-w-[140px] truncate">{primerHuesped}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="text-slate-300">{r.responsable_nombre || "—"}</span>
                    {r.responsable_ruc && <span className="block text-slate-500 text-[11px]">{r.responsable_ruc}</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-emerald-400 font-mono text-xs whitespace-nowrap">
                    Gs. {(Number(r.total_general)||0).toLocaleString("es-PY")}
                  </td>
                  {canExtracto && (
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => onExtracto(r.id)}
                        className="p-1.5 rounded-lg bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 transition-colors"
                        title="Ver cuenta">
                        <Receipt className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {filtrado.length === 0 && !loading && (
              <tr><td colSpan={canExtracto ? 8 : 7} className="px-4 py-12 text-center text-slate-500">
                {q ? "Sin resultados para esa búsqueda" : "Sin reservas en el período seleccionado"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Modal({ onClose, title, icon, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`bg-arandu-dark-light border border-white/10 rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">{icon}<h3 className="text-base font-bold text-white">{title}</h3></div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// Autocompletado de huésped: huéspedes anteriores (memoria del hotel) + clientes del Catastro
function HuespedNombreInput({ value, onChange, onPick, clientes }) {
  const { token, activeEmpresaPropia } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [frec, setFrec] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = (value || "").trim();
    if (!open || q.length < 2) { setFrec([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const eq = activeEmpresaPropia?.id ? `&empresa_id=${encodeURIComponent(activeEmpresaPropia.id)}` : "";
        const r = await fetch(`${API}/admin/hotel/huespedes/buscar?q=${encodeURIComponent(q)}${eq}`, { headers: { Authorization: `Bearer ${token}` } });
        setFrec(r.ok ? await r.json() : []);
      } catch { setFrec([]); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [value, open, token, activeEmpresaPropia?.id]);

  const sugerencias = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (q.length < 2) return [];
    const out = frec.map(h => ({
      key: "h" + h.id, origen: "Huésped anterior",
      etiqueta: h.nombre, detalle: [h.tipo_doc, h.nro_doc].filter(Boolean).join(" "),
      datos: { nombre: h.nombre || "", tipo_doc: h.tipo_doc || "C.I.P.", nro_doc: h.nro_doc || "",
               celular: h.celular || "", nacionalidad: h.nacionalidad || "Paraguaya", procedencia: h.procedencia || "" },
    }));
    const vistos = new Set(out.map(o => (o.datos.nro_doc || o.datos.nombre).toLowerCase()));
    (Array.isArray(clientes) ? clientes : []).forEach(c => {
      const nom = c.nombre || c.razon_social || "";
      if (!(nom.toLowerCase().includes(q) || (c.razon_social || "").toLowerCase().includes(q) || (c.ruc || "").toLowerCase().includes(q))) return;
      const k = (c.ruc || nom).toLowerCase();
      if (vistos.has(k)) return;
      vistos.add(k);
      out.push({
        key: "c" + c.id, origen: "Catastro", etiqueta: nom,
        detalle: [c.tipo_documento, c.ruc].filter(Boolean).join(" "),
        datos: { nombre: nom, tipo_doc: c.tipo_documento === "CI" ? "C.I.P." : (c.tipo_documento || "C.I.P."),
                 nro_doc: c.ruc || "", celular: c.telefono || "", nacionalidad: c.nacionalidad || "Paraguaya", procedencia: c.ciudad || "" },
      });
    });
    return out.slice(0, 8);
  }, [value, frec, clientes]);

  return (
    <div className="relative">
      <input className={INPUT} value={value} placeholder="Nombre completo (escribí para buscar)"
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && sugerencias.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-56 overflow-y-auto">
          {sugerencias.map(sg => (
            <button type="button" key={sg.key} onMouseDown={e => e.preventDefault()}
              onClick={() => { onPick(sg.datos); setOpen(false); }}
              className="w-full flex justify-between items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5">
              <span className="text-white truncate">{sg.etiqueta}<span className="text-slate-500 text-xs ml-2">{sg.detalle}</span></span>
              <span className="text-[10px] text-cyan-400 shrink-0">{sg.origen}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs text-slate-400 mb-1">{label}</label>{children}</div>;
}

const INPUT = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/60";
const BTN_PRI = "flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors font-semibold";
const BTN_SEC = "px-4 py-2 bg-white/5 text-slate-400 rounded-lg text-sm hover:bg-white/10 transition-colors";
